/* eslint-disable indent, no-nested-ternary, no-unused-expressions, jsx-a11y/no-autofocus, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/role-supports-aria-props */
import React, {
	CSSProperties,
	FC,
	FormEvent,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
	FaArrowRight,
	FaBan,
	FaCheck,
	FaCheckCircle,
	FaChevronDown,
	FaChevronUp,
	FaExclamationTriangle,
	FaExternalLinkAlt,
	FaDownload,
	FaFileAlt,
	FaHistory,
	FaPaperPlane,
	FaPen,
	FaRedo,
	FaRobot,
	FaShieldAlt,
	FaSpinner,
	FaStop,
	FaTimes,
	FaUndo,
} from 'react-icons/fa';
import { conversationalAssistantText } from './conversationalText';
import { useAutopilot } from './AutopilotContext';
import { useMultiView } from '../multiView/MultiViewContext';
import { isolateModalBackground } from './modalAccessibility';
import {
	AutopilotAction,
	AutopilotActionPreview,
	AutopilotArtifact,
	AutopilotCitation,
	AutopilotMessage,
	AutopilotPreviewItem,
	AutopilotStructuredPreview,
	AutomationMode,
	BidderActionItemMessageBatchPreview,
	BidderResponseSourceCoverage,
	requiresAutopilotAttention,
} from './types';
import {
	createAutopilotCancelSource,
	getAutopilotArtifact,
	getAutopilotRecentActions,
	loadAutopilotArtifact,
	loadOwnerFurnishedEquipmentReport,
	retryAutopilotArtifact,
} from './api';
import { ActivityNotification, ActivityNotificationDetail } from '../activityNotifications/types';
import PublishProjectModal from '../bidInvites/PublishProjectModal';
import { PublishProjectOptions } from '../bidInvites/publishApi';
import AutopilotTransientPreview from './AutopilotTransientPreview';
import BidderActionItemMessageBatch, {
	bidderActionItemMessageBatchApprovalPayload,
	bidderActionItemMessageBatchIssue,
} from './BidderActionItemMessageBatch';
import { cleanLegacyActionItemText } from '../../utils/actionItemDisplayText';
import {
	AutopilotReviewSurfaceProvider,
	useAutopilotReviewSurfaces,
} from './AutopilotReviewSurfaceRegistry';
import './Autopilot.css';

const MIN_AUTOPILOT_RAIL_WIDTH = 360;
const MAX_AUTOPILOT_RAIL_WIDTH = 720;
const MOBILE_AUTOPILOT_BREAKPOINT = 767;
const DOCKED_AUTOPILOT_BREAKPOINT = 1280;
const MULTI_VIEW_MIN_WORKSPACE_WIDTH = 652;
const LAUNCHER_EDGE_GAP = 12;
const AUTOPILOT_CHAT_FONT_SIZE_STORAGE_KEY = 'scrubplan.autopilot.chatFontSize';
const DEFAULT_AUTOPILOT_CHAT_FONT_SIZE = 11;
const MIN_AUTOPILOT_CHAT_FONT_SIZE = 10;
const MAX_AUTOPILOT_CHAT_FONT_SIZE = 16;

const readAutopilotChatFontSize = () => {
	if (typeof window === 'undefined') return DEFAULT_AUTOPILOT_CHAT_FONT_SIZE;
	try {
		const stored = Number(window.localStorage.getItem(AUTOPILOT_CHAT_FONT_SIZE_STORAGE_KEY));
		return Number.isInteger(stored) &&
			stored >= MIN_AUTOPILOT_CHAT_FONT_SIZE &&
			stored <= MAX_AUTOPILOT_CHAT_FONT_SIZE
			? stored
			: DEFAULT_AUTOPILOT_CHAT_FONT_SIZE;
	} catch (_error) {
		return DEFAULT_AUTOPILOT_CHAT_FONT_SIZE;
	}
};

export interface AutopilotLauncherBounds {
	bottom: number;
	left: number;
	right: number;
	top: number;
}

export const getAutopilotLauncherPlacement = ({
	launcherHeight,
	launcherWidth,
	mobile,
	obstructions,
	reviewerMounted,
	viewportHeight,
	viewportWidth,
}: {
	launcherHeight: number;
	launcherWidth: number;
	mobile: boolean;
	obstructions: AutopilotLauncherBounds[];
	reviewerMounted: boolean;
	viewportHeight: number;
	viewportWidth: number;
}): { right: number; top: number } => {
	const defaultRight = mobile ? 12 : 0;
	const defaultTop = reviewerMounted ? (mobile ? 144 : 164) : mobile ? 78 : 88;
	const maximumTop = Math.max(
		LAUNCHER_EDGE_GAP,
		viewportHeight - launcherHeight - LAUNCHER_EDGE_GAP,
	);
	const baseTop = Math.min(defaultTop, maximumTop);
	const relevant = obstructions.filter(
		(bounds) =>
			bounds.right > viewportWidth / 2 &&
			bounds.bottom > baseTop &&
			bounds.top < baseTop + launcherHeight,
	);
	if (!relevant.length) return { right: defaultRight, top: baseTop };

	const obstruction = relevant.reduce(
		(combined, bounds) => ({
			bottom: Math.max(combined.bottom, bounds.bottom),
			left: Math.min(combined.left, bounds.left),
			right: Math.max(combined.right, bounds.right),
			top: Math.min(combined.top, bounds.top),
		}),
		relevant[0],
	);
	const belowTop = Math.max(baseTop, obstruction.bottom + LAUNCHER_EDGE_GAP);
	if (belowTop <= maximumTop) return { right: defaultRight, top: belowTop };

	const leftOfObstruction = obstruction.left - LAUNCHER_EDGE_GAP - launcherWidth;
	if (leftOfObstruction >= LAUNCHER_EDGE_GAP) {
		return {
			right: Math.max(LAUNCHER_EDGE_GAP, viewportWidth - leftOfObstruction - launcherWidth),
			top: Math.max(LAUNCHER_EDGE_GAP, Math.min(maximumTop, Math.max(baseTop, obstruction.top))),
		};
	}

	const aboveTop = obstruction.top - LAUNCHER_EDGE_GAP - launcherHeight;
	return {
		right: defaultRight,
		top: aboveTop >= LAUNCHER_EDGE_GAP ? aboveTop : maximumTop,
	};
};

const actionUpdatedAt = (action: AutopilotAction) =>
	Date.parse(action.updatedAt || action.decidedAt || action.createdAt || '') || 0;

export const mergeAutopilotActionsByIdentity = (
	...collections: Array<AutopilotAction[] | null | undefined>
): AutopilotAction[] => {
	const merged = new Map<string, AutopilotAction>();
	collections.forEach((collection) => {
		(collection || []).forEach((action) => {
			const key = String(action.id);
			const current = merged.get(key);
			if (
				!current ||
				Number(action.version || 0) > Number(current.version || 0) ||
				(Number(action.version || 0) === Number(current.version || 0) &&
					actionUpdatedAt(action) >= actionUpdatedAt(current))
			) {
				merged.set(key, action);
			}
		});
	});
	return Array.from(merged.values());
};

export const isInteractiveReviewerPath = (pathname: string): boolean =>
	['/multi-package-interactive-review/', '/exclusivity-review/'].some(
		(prefix) => String(pathname || '').indexOf(prefix) === 0,
	);
const AUTOPILOT_SEPARATOR_WIDTH = 12;
const PANEL_FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const modeLabel = (mode: AutomationMode) =>
	mode === 'SELF_DRIVING' ? 'Self-driving' : mode.charAt(0) + mode.slice(1).toLowerCase();

const statusLabel = (status: string) =>
	status
		.toLowerCase()
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

const formatTime = (value?: string | null) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		month: 'short',
	}).format(date);
};

const citationHrefIsExternal = (url: string) => /^https?:\/\//i.test(url);
const safeCitationHref = (value?: string | null) => {
	const url = String(value || '').trim();
	return /^https?:\/\//i.test(url) || /^\/(?!\/)/.test(url) ? url : null;
};

const INTERNAL_FINGERPRINT = /^(?:sha(?:1|256|512):?)?[a-f0-9]{32,}$/i;

const cleanCitationText = (value?: string | null) =>
	String(value || '')
		.replace(/\b(?:sha(?:1|256|512):?)?[a-f0-9]{32,}\b/gi, '')
		.split(/\s*[·|]\s*/)
		.map((part) => part.trim())
		.filter((part) => part && !INTERNAL_FINGERPRINT.test(part))
		.join(' · ');

const publicCitations = (citations: AutopilotCitation[]): AutopilotCitation[] => {
	const grouped = new Map<
		string,
		AutopilotCitation & { labels: Set<string>; locations: Set<string> }
	>();
	citations.forEach((citation) => {
		const rawVersion = String(citation.version || '').trim();
		const fingerprintKey = INTERNAL_FINGERPRINT.test(rawVersion)
			? `fingerprint:${rawVersion.toLowerCase()}`
			: '';
		const key =
			fingerprintKey ||
			String(citation.url || '')
				.trim()
				.toLowerCase() ||
			String(citation.documentName || '')
				.trim()
				.toLowerCase() ||
			`${String(citation.sourceType || '').toLowerCase()}:${String(
				citation.label || '',
			).toLowerCase()}`;
		const label = cleanCitationText(citation.documentName || citation.label) || 'Source';
		const location = cleanCitationText(citation.location);
		const existing = grouped.get(key);
		if (existing) {
			existing.labels.add(label);
			if (location) existing.locations.add(location);
			return;
		}
		grouped.set(key, {
			...citation,
			label,
			location: location || null,
			version: cleanCitationText(citation.version) || null,
			labels: new Set([label]),
			locations: new Set(location ? [location] : []),
		});
	});
	return Array.from(grouped.values()).map(({ labels, locations, ...citation }) => ({
		...citation,
		label: Array.from(labels).join(' · '),
		location: Array.from(locations).join('; ') || null,
	}));
};

const Citations: FC<{ citations?: AutopilotCitation[] }> = ({ citations = [] }) => {
	const visibleCitations = publicCitations(citations);
	if (!visibleCitations.length) return null;
	return (
		<div className="autopilot-citations" aria-label="References">
			<span className="autopilot-citations__label">References</span>
			{visibleCitations.map((citation) => {
				const copy = [citation.location, citation.version].filter(Boolean).join(' · ');
				const href = safeCitationHref(citation.url);
				return href ? (
					<a
						className="autopilot-citation"
						href={href}
						key={citation.id}
						rel={citationHrefIsExternal(href) ? 'noopener noreferrer' : undefined}
						target={citationHrefIsExternal(href) ? '_blank' : undefined}
						title={citation.documentName || citation.label}
					>
						<span>{citation.label}</span>
						{copy && <small>{copy}</small>}
						<FaExternalLinkAlt aria-hidden="true" />
					</a>
				) : (
					<span className="autopilot-citation" key={citation.id}>
						<span>{citation.label}</span>
						{copy && <small>{copy}</small>}
					</span>
				);
			})}
		</div>
	);
};

const SOURCE_COVERAGE_ORDER = [
	'SCOPE_CHECKLIST',
	'DRAWINGS',
	'SPECIFICATIONS',
	'SUPPLEMENTAL_BID_DOCUMENTS',
] as const;

const sourceCoverageLabel = (category: string) => {
	switch (category) {
		case 'SCOPE_CHECKLIST':
			return 'Package scope checklist';
		case 'DRAWINGS':
			return 'Current drawings';
		case 'SPECIFICATIONS':
			return 'Current specifications';
		case 'SUPPLEMENTAL_BID_DOCUMENTS':
			return 'Supplemental bid documents';
		default:
			return 'Project source';
	}
};

const sourceCoverageStatus = (status: string, category: string) => {
	switch (status) {
		case 'CHECKED_MATCH':
			return { label: 'Supporting source found', tone: 'match' };
		case 'CHECKED_NO_MATCH':
			return { label: 'Checked — no supporting match', tone: 'no-match' };
		case 'NOT_PRESENT':
			return {
				label: category === 'SCOPE_CHECKLIST' ? 'No checklist configured' : 'No current documents',
				tone: 'not-present',
			};
		case 'UNAVAILABLE':
			return { label: 'Temporarily unavailable', tone: 'unavailable' };
		default:
			return { label: 'Status unavailable', tone: 'unavailable' };
	}
};

const SourceCoverage: FC<{ preview: AutopilotStructuredPreview }> = ({ preview }) => {
	const raw = Array.isArray(preview.sourceCoverage) ? preview.sourceCoverage : [];
	if (!raw.length) return null;
	const rows = SOURCE_COVERAGE_ORDER.map((category) =>
		raw.find((entry) => entry && entry.category === category),
	).filter(Boolean) as BidderResponseSourceCoverage[];
	if (!rows.length) return null;
	return (
		<section className="autopilot-source-coverage" aria-label="Project sources checked">
			<div className="autopilot-source-coverage__heading">
				<strong>Project sources checked</strong>
				<small>Internal review — not included in the bidder message</small>
			</div>
			<ul>
				{rows.map((row) => {
					const status = sourceCoverageStatus(row.status, row.category);
					return (
						<li key={row.category}>
							<span>{sourceCoverageLabel(row.category)}</span>
							<strong className={`is-${status.tone}`}>{status.label}</strong>
						</li>
					);
				})}
			</ul>
		</section>
	);
};

const isStructuredPreview = (
	preview: AutopilotActionPreview | null | undefined,
): preview is AutopilotStructuredPreview =>
	typeof preview === 'object' && preview !== null && !Array.isArray(preview);

type InitialAutoAnalysisOptionKey =
	| 'acceptSuggestedTrades'
	| 'createBidLevelingChecklists'
	| 'proceedWithBidListAgent';

type InitialAutoAnalysisOptions = Record<InitialAutoAnalysisOptionKey, boolean>;

const DEFAULT_INITIAL_AUTO_ANALYSIS_OPTIONS: InitialAutoAnalysisOptions = {
	acceptSuggestedTrades: true,
	createBidLevelingChecklists: true,
	proceedWithBidListAgent: true,
};

const initialAutoAnalysisOptions = (
	preview: AutopilotStructuredPreview,
): InitialAutoAnalysisOptions => {
	const { options } = preview;
	if (!options) return { ...DEFAULT_INITIAL_AUTO_ANALYSIS_OPTIONS };
	const acceptSuggestedTrades = options.acceptSuggestedTrades !== false;
	const createBidLevelingChecklists =
		acceptSuggestedTrades && options.createBidLevelingChecklists !== false;
	return {
		acceptSuggestedTrades,
		createBidLevelingChecklists,
		proceedWithBidListAgent:
			createBidLevelingChecklists && options.proceedWithBidListAgent !== false,
	};
};

const updateInitialAutoAnalysisOption = (
	current: InitialAutoAnalysisOptions,
	key: InitialAutoAnalysisOptionKey,
	checked: boolean,
): InitialAutoAnalysisOptions => {
	if (key === 'acceptSuggestedTrades') {
		return checked
			? { ...current, acceptSuggestedTrades: true }
			: {
					acceptSuggestedTrades: false,
					createBidLevelingChecklists: false,
					proceedWithBidListAgent: false,
			  };
	}
	if (key === 'createBidLevelingChecklists') {
		return checked
			? { ...current, acceptSuggestedTrades: true, createBidLevelingChecklists: true }
			: {
					...current,
					createBidLevelingChecklists: false,
					proceedWithBidListAgent: false,
			  };
	}
	return checked
		? {
				acceptSuggestedTrades: true,
				createBidLevelingChecklists: true,
				proceedWithBidListAgent: true,
		  }
		: { ...current, proceedWithBidListAgent: false };
};

const editablePreviewForAction = (action: AutopilotAction): AutopilotActionPreview => {
	const preview = action.preview || '';
	if (action.type !== 'A1_START_INITIAL_AUTO_ANALYSIS' || !isStructuredPreview(preview)) {
		return preview;
	}
	return { ...preview, options: initialAutoAnalysisOptions(preview) };
};

const actionDisplayTitle = (action: AutopilotAction) =>
	action.type === 'A9_REQUEST_ALTERNATE_MATERIAL_PRICE'
		? `Send ${action.bidderName || 'this bidder'} a private material-pricing question?`
		: action.question || action.title;

const displayPreviewValue = (value: unknown): string => {
	if (value === undefined || value === null) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (Array.isArray(value)) {
		return value
			.map((item) => displayPreviewValue(item))
			.filter(Boolean)
			.join('\n');
	}
	if (typeof value === 'object') {
		return Object.entries(value as Record<string, unknown>)
			.filter(([, item]) => ['string', 'number', 'boolean'].includes(typeof item))
			.map(([key, item]) => {
				const label = key
					.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
					.replace(/[_-]+/g, ' ')
					.replace(/^./, (character) => character.toUpperCase());
				return `${label}: ${String(item)}`;
			})
			.filter(Boolean)
			.join('\n');
	}
	return '';
};

const previewItemLabel = (item: AutopilotPreviewItem, index: number) => {
	const candidate = [
		item.displayText,
		item.requirementText,
		item.title,
		item.label,
		item.text,
		item.description,
		item.checklistText,
	].find((value) => typeof value === 'string' && value.trim());
	const fallback =
		candidate ||
		Object.entries(item)
			.filter(
				([key, value]) =>
					!/(?:^id$|id$|key$|token$)/i.test(key) &&
					(typeof value === 'string' || typeof value === 'number'),
			)
			.map(([, value]) => String(value).trim())
			.find(Boolean);
	return cleanLegacyActionItemText(fallback) || `Review item ${index + 1}`;
};

const a1PreviewItemLabel = (item: AutopilotPreviewItem, index: number): string => {
	const value = [item.description, item.descriptionOfChange, item.scopeItem, item.text].find(
		(candidate) => typeof candidate === 'string' && candidate.trim(),
	);
	return typeof value === 'string' ? value : `Change item ${index + 1}`;
};

const bidListContactLabel = (item: AutopilotPreviewItem): string =>
	[item.contactName, item.contactEmail, item.contactPhone]
		.filter((value) => typeof value === 'string' && value.trim())
		.map(String)
		.join(' · ');

const quoteAlternateDetail = (item: AutopilotPreviewItem): string => {
	const priceIsTbd = String(item.pricingStatus || '').toUpperCase() === 'TBD';
	const price = priceIsTbd
		? 'Price TBD'
		: typeof item.sourcePriceSummary === 'string' && item.sourcePriceSummary.trim()
		? item.sourcePriceSummary.trim()
		: '';
	return [price, item.outcome]
		.filter((value) => typeof value === 'string' && value.trim())
		.map(String)
		.filter((value, index, values) => values.indexOf(value) === index)
		.join(' · ');
};

const quoteAlternateDescription = (item: AutopilotPreviewItem, index: number): string => {
	const value = item.approvedDescription ?? item.description;
	return typeof value === 'string' && value.trim() ? value : previewItemLabel(item, index);
};

const previewEntries = (
	preview: AutopilotStructuredPreview,
): { entries: AutopilotPreviewItem[]; key: 'items' | 'rows' | null } =>
	Array.isArray(preview.items)
		? { entries: preview.items, key: 'items' as const }
		: Array.isArray(preview.rows)
		? { entries: preview.rows, key: 'rows' as const }
		: { entries: [], key: null };

const quoteAlternateSelectionPayload = (
	preview: AutopilotStructuredPreview,
): AutopilotStructuredPreview => {
	const { entries } = previewEntries(preview);
	return {
		kind: 'QUOTE_ALTERNATE_BATCH',
		rows: entries
			.filter((item) => item.id !== undefined && item.id !== null)
			.map((item) => ({
				approvedDescription: String(item.approvedDescription ?? item.description ?? ''),
				id: String(item.id),
				selected: item.selected !== false,
			})),
	};
};

const quoteScopeInclusionSelectionPayload = (
	preview: AutopilotStructuredPreview,
): AutopilotStructuredPreview => {
	const { entries } = previewEntries(preview);
	return {
		kind: 'QUOTE_SCOPE_INCLUSION_BATCH',
		rows: entries
			.filter((item) => item.id !== undefined && item.id !== null)
			.map((item) => ({
				id: String(item.id),
				selected: item.selected !== false,
				groupingName: String(item.groupingName || '').trim(),
				checklistText: String(item.checklistText || '').trim(),
			})),
	};
};

const bidderCostPlacementSelectionPayload = (
	preview: AutopilotStructuredPreview,
): AutopilotStructuredPreview => {
	const { entries } = previewEntries(preview);
	const version = Number(preview.snapshotVersion || 2);
	return {
		kind:
			version >= 3 ? 'BID_LEVELING_COST_PLACEMENT_SELECTION' : 'BID_LEVELING_COST_PLACEMENT_BATCH',
		rows: entries
			.filter((item) => item.id !== undefined && item.id !== null)
			.map((item) => ({
				id: String(item.id),
				selected: item.selected !== false,
				...(version >= 3 && item.targetKey ? { targetKey: String(item.targetKey) } : {}),
			})),
	};
};

const packageBidderSelectionPayload = (
	preview: AutopilotStructuredPreview,
): AutopilotStructuredPreview => {
	const { entries } = previewEntries(preview);
	return {
		kind: 'BID_LIST_SELECTIONS',
		items: entries
			.filter((item) => item.disabled !== true && item.id !== undefined && item.id !== null)
			.map((item) => ({
				id: String(item.id),
				selected: item.selected !== false,
			})),
	};
};

const RecipientSummary: FC<{ preview: AutopilotStructuredPreview }> = ({ preview }) => {
	const summary = preview.recipientSummary;
	const recipientValues = summary && Array.isArray(summary.recipients) ? summary.recipients : [];
	const recipients = recipientValues.filter(
		(recipient) =>
			recipient &&
			(typeof recipient.companyName === 'string' || recipient.invitationId !== undefined),
	);
	const excluded = summary && Array.isArray(summary.excluded) ? summary.excluded : [];
	if (!summary || (recipients.length === 0 && excluded.length === 0)) return null;
	const label =
		typeof summary.label === 'string' && summary.label.trim()
			? summary.label
			: 'Recipients captured when this card was created';
	return (
		<section className="autopilot-recipient-summary" aria-label={label}>
			<strong>{label}</strong>
			{recipients.length > 0 && (
				<ul>
					{recipients.map((recipient, index) => (
						<li
							key={`${String(recipient.invitationId ?? '')}:${String(
								recipient.vendorContactId ?? recipient.companyName ?? index,
							)}`}
						>
							{typeof recipient.companyName === 'string' && recipient.companyName.trim()
								? recipient.companyName
								: `Bidder invitation ${String(recipient.invitationId)}`}
							{recipient.contactName ? ` · ${recipient.contactName}` : ''}
							{recipient.email ? ` · ${recipient.email}` : ''}
							{recipient.status ? ` · ${recipient.status}` : ''}
						</li>
					))}
				</ul>
			)}
			{excluded.length > 0 && (
				<details className="autopilot-recipient-summary__excluded">
					<summary>Excluded ({excluded.length})</summary>
					<ul>
						{excluded.map((recipient, index) => (
							<li
								key={`${String(recipient.invitationId ?? '')}:${String(
									recipient.vendorContactId ?? recipient.email ?? recipient.companyName ?? index,
								)}:excluded`}
							>
								{recipient.companyName || recipient.contactName || recipient.email || 'Bidder'}
								{recipient.reason ? ` — ${recipient.reason}` : ''}
							</li>
						))}
					</ul>
				</details>
			)}
		</section>
	);
};

const OwnerFurnishedEquipmentPreview: FC<{ preview: AutopilotStructuredPreview }> = ({
	preview,
}) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const projectId = Number(preview.projectId);
	const scanRunId = Number(preview.scanRunId);
	const sourceReferences = Array.isArray(preview.sourceReferences) ? preview.sourceReferences : [];
	const sourceReferenceLabel = (reference: any) => {
		const details = [
			reference.fileOrProjectArea,
			reference.sheet ? `Sheet ${reference.sheet}` : '',
			reference.detail ? `Detail ${reference.detail}` : '',
			reference.note ? `Note ${reference.note}` : '',
			reference.page ? `Page ${reference.page}` : '',
			reference.specificationSection ? `Section ${reference.specificationSection}` : '',
		].filter(Boolean);
		return `${reference.sourceType === 'SPECIFICATION' ? 'Specification' : 'Drawing'}: ${
			details.join(', ') || 'Reference available'
		}`;
	};
	const openReport = async (download: boolean) => {
		setBusy(true);
		setError('');
		try {
			const blob = await loadOwnerFurnishedEquipmentReport(projectId, scanRunId);
			const url = window.URL.createObjectURL(blob);
			if (download) {
				const anchor = document.createElement('a');
				anchor.href = url;
				anchor.download = 'Owner-Furnished - Owner-Scope Requirements Report.pdf';
				document.body.appendChild(anchor);
				anchor.click();
				anchor.remove();
			} else {
				window.open(url, '_blank', 'noopener,noreferrer');
			}
			window.setTimeout(() => window.URL.revokeObjectURL(url), download ? 1000 : 60000);
		} catch (_requestError) {
			setError('The owner-furnished equipment report could not be opened.');
		} finally {
			setBusy(false);
		}
	};
	return (
		<div className="autopilot-owner-furnished-preview">
			<strong>Owner-Furnished / Owner-Scope Requirements Report</strong>
			<p>
				{String(
					preview.summary ||
						'Review the detected OFOI, OFCI, Owner Coordination, and Owner-Performed Scope requirements.',
				)}
			</p>
			<div>
				<span>OFOI / OFE: {Number(preview.ofoiCount || 0)}</span>
				<span>OFCI: {Number(preview.ofciCount || 0)}</span>
				{preview.ownerScopeCount !== undefined && (
					<span>Owner scope: {Number(preview.ownerScopeCount || 0)}</span>
				)}
			</div>
			{preview.expectedSheetCount !== undefined && (
				<div aria-label="Owner-scope sheet coverage">
					<span>
						Sheets: {Number(preview.completedSheetCount || 0)} of{' '}
						{Number(preview.expectedSheetCount || 0)} completed
					</span>
					<span>Failed: {Number(preview.failedSheetCount || 0)}</span>
				</div>
			)}
			{preview.sourceCoverage && !Array.isArray(preview.sourceCoverage) && (
				<div aria-label="Owner-furnished source coverage">
					<span>
						Drawings: {String(preview.sourceCoverage.drawings || 'Unknown')} ·{' '}
						{Number(preview.drawingFindingCount || 0)} findings
					</span>
					<span>
						Specifications: {String(preview.sourceCoverage.specifications || 'Unknown')} ·{' '}
						{Number(preview.specificationFindingCount || 0)} findings
					</span>
				</div>
			)}
			{preview.coverageWarning && (
				<p className="autopilot-owner-furnished-preview__warning" role="status">
					{String(preview.coverageWarning)}
				</p>
			)}
			{sourceReferences.length > 0 && (
				<div className="autopilot-owner-furnished-preview__references">
					<strong>
						Source references ({Number(preview.sourceReferenceCount || sourceReferences.length)})
					</strong>
					<ul>
						{sourceReferences.map((reference: any, index: number) => (
							<li key={`${sourceReferenceLabel(reference)}:${index}`}>
								{sourceReferenceLabel(reference)}
							</li>
						))}
					</ul>
				</div>
			)}
			{preview.drawingVersionName && (
				<small>Drawing version: {String(preview.drawingVersionName)}</small>
			)}
			<div className="autopilot-owner-furnished-preview__actions">
				<button type="button" disabled={busy} onClick={() => openReport(false)}>
					{busy ? 'Opening…' : 'Review report'}
				</button>
				<button type="button" disabled={busy} onClick={() => openReport(true)}>
					<FaDownload aria-hidden="true" /> Download
				</button>
			</div>
			{error && <span role="alert">{error}</span>}
		</div>
	);
};

const Preview: FC<{ preview: AutopilotActionPreview; hideComparison?: boolean }> = ({
	preview,
	hideComparison = false,
}) => {
	if (typeof preview === 'string') {
		if (hideComparison && preview.indexOf('Drawings ready; analysis not started') >= 0) {
			return null;
		}
		return <pre>{preview}</pre>;
	}
	// Older queued refresh cards can still carry internal source signatures.
	// Never render those hashes or serialized fingerprints in Autopilot Chat.
	if (preview.kind === 'SOURCE_FINGERPRINT') {
		return (
			<p className="autopilot-structured-preview">
				Scope documents changed. Refresh the affected checklist from the current version.
			</p>
		);
	}
	if (preview.kind === 'OWNER_FURNISHED_EQUIPMENT_REPORT') {
		return <OwnerFurnishedEquipmentPreview preview={preview} />;
	}
	if (preview.kind === 'BIDDER_MESSAGE') {
		const message =
			preview.after && typeof preview.after === 'object' && !Array.isArray(preview.after)
				? (preview.after as Record<string, unknown>)
				: {};
		const channels = Array.isArray(message.channels) ? message.channels : [];
		const isInternalTeamDraft = channels.map(String).includes('TEAM_ONLY');
		const messageItems = Array.isArray(preview.items) ? preview.items : [];
		const sourceQuestion =
			preview.before && typeof preview.before === 'object' && !Array.isArray(preview.before)
				? (preview.before as Record<string, unknown>)
				: null;
		return (
			<div className="autopilot-bidder-message-preview">
				{isInternalTeamDraft && (
					<p className="autopilot-bidder-message-preview__internal-note">
						Internal project-team draft — nothing is sent to the bidder
					</p>
				)}
				{sourceQuestion && typeof sourceQuestion.body === 'string' && sourceQuestion.body.trim() && (
					<section
						className="autopilot-bidder-message-preview__question"
						aria-label="Question asked"
					>
						<span>Question asked</span>
						{typeof sourceQuestion.subject === 'string' && sourceQuestion.subject.trim() && (
							<strong>{sourceQuestion.subject}</strong>
						)}
						<p>{sourceQuestion.body}</p>
						{typeof sourceQuestion.channel === 'string' && sourceQuestion.channel.trim() && (
							<small>{sourceQuestion.channel}</small>
						)}
					</section>
				)}
				<SourceCoverage preview={preview} />
				{typeof message.subject === 'string' && message.subject.trim() && (
					<div>
						<span>Subject</span>
						<strong>{message.subject}</strong>
					</div>
				)}
				{typeof message.body === 'string' && message.body.trim() && (
					<div>
						<span>{isInternalTeamDraft ? 'Internal draft' : 'Message'}</span>
						<p>{message.body}</p>
					</div>
				)}
				{channels.length > 0 && (
					<div
						className="autopilot-bidder-message-preview__channels"
						aria-label="Delivery channels"
					>
						{channels.map((channel) => (
							<small key={String(channel)}>{String(channel)}</small>
						))}
					</div>
				)}
				<RecipientSummary preview={preview} />
				{messageItems.length > 0 && (
					<ul className="autopilot-structured-preview__items">
						{messageItems.map((item, index) => (
							<li key={String(item.id === undefined ? index : item.id)}>
								<span className="autopilot-structured-preview__selection" aria-hidden="true">
									<FaCheck />
								</span>
								<div>
									<strong>{previewItemLabel(item, index)}</strong>
									{typeof item.description === 'string' && item.description.trim() && (
										<small>{item.description}</small>
									)}
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		);
	}
	if (preview.kind === 'BID_LEVELING_REPLY_DIFF') {
		const resolutions = Array.isArray(preview.items) ? preview.items : [];
		const mutations = Array.isArray(preview.mutations)
			? (preview.mutations as AutopilotPreviewItem[])
			: [];
		return (
			<div className="autopilot-reply-resolution-preview">
				{resolutions.length > 0 && (
					<section aria-label="Answered action items">
						<h4>Action items satisfied by the bidder’s response</h4>
						<p>ScrubPlan recommends removing these items from the outstanding action list.</p>
						<ul>
							{resolutions.map((item, index) => (
								<li key={String(item.id === undefined ? index : item.id)}>
									<strong>{String(item.question || previewItemLabel(item, index))}</strong>
									{typeof item.recommendation === 'string' && <span>{item.recommendation}</span>}
								</li>
							))}
						</ul>
					</section>
				)}
				<section aria-label="Proposed Bid Leveling changes">
					<h4>Proposed Bid Leveling changes</h4>
					{mutations.length ? (
						<ul>
							{mutations.map((item, index) => (
								<li key={String(item.id === undefined ? index : item.id)}>
									<strong>{previewItemLabel(item, index)}</strong>
									<span>
										{displayPreviewValue(item.before) || 'No current value'} →{' '}
										{displayPreviewValue(item.after) || 'Clear value'}
									</span>
								</li>
							))}
						</ul>
					) : (
						<p>No checklist answer needs to change.</p>
					)}
				</section>
			</div>
		);
	}
	if (preview.kind === 'EXCLUDED_SCOPE_COVERAGE') {
		const recommendationKind = String(preview.recommendationKind || '');
		const matchingBidders = Array.isArray(preview.matchingBidders)
			? (preview.matchingBidders as AutopilotPreviewItem[])
			: [];
		const matchingPackages = Array.isArray(preview.matchingPackages)
			? (preview.matchingPackages as AutopilotPreviewItem[])
			: [];
		return (
			<div className="autopilot-governed-operation-preview">
				<div className="autopilot-governed-operation-preview__context">
					{typeof preview.sourcePackageName === 'string' && (
						<span>Source package: {preview.sourcePackageName}</span>
					)}
					{typeof preview.bidderName === 'string' && <span>Excluded by: {preview.bidderName}</span>}
				</div>
				{typeof preview.sourceLine === 'string' && preview.sourceLine.trim() && (
					<h4 className="autopilot-governed-operation-preview__scope">{preview.sourceLine}</h4>
				)}
				{recommendationKind === 'SAME_PACKAGE_COVERAGE' && (
					<section aria-label="Other bidders with coverage">
						<strong>Other bidders with coverage</strong>
						<p>
							{matchingBidders
								.map((row) => String(row.bidderName || row.companyName || ''))
								.filter(Boolean)
								.join(', ')}
						</p>
					</section>
				)}
				{recommendationKind === 'EXISTING_OTHER_PACKAGE' && (
					<section aria-label="Matching package checklists">
						<strong>Matching package checklists</strong>
						<p>
							{matchingPackages
								.map((row) => String(row.packageName || row.bidPackageName || ''))
								.filter(Boolean)
								.join(', ')}
						</p>
					</section>
				)}
				{recommendationKind === 'ADD_TO_RECOMMENDED_PACKAGE' && (
					<section aria-label="Recommended package">
						<strong>Recommended package</strong>
						<p>{String(preview.targetPackageName || '')}</p>
						{preview.targetGroupName && (
							<small>Checklist group: {String(preview.targetGroupName)}</small>
						)}
						<small>Active recipients: {Number(preview.recipientCount || 0)}</small>
					</section>
				)}
				{typeof preview.rationale === 'string' && preview.rationale.trim() && (
					<p>
						<b>Rationale</b>
						{preview.rationale}
					</p>
				)}
			</div>
		);
	}
	if (preview.kind === 'BID_LEVELING_COST_PLACEMENT_BATCH') {
		const rows = Array.isArray(preview.rows) ? preview.rows : [];
		const money = (value: unknown) =>
			Number(value).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
		return (
			<div className="autopilot-cost-placement-preview">
				<p>Only the selected bidder-stated costs will be placed in Bid Leveling.</p>
				<ul>
					{rows.map((row, index) => (
						<li
							className={row.selected === false ? 'is-excluded' : ''}
							key={String(row.id ?? index)}
						>
							<strong>{String(row.requirementText || `Cost item ${index + 1}`)}</strong>
							<span>{String(row.destination || 'Bid Leveling cost')}</span>
							{row.disabled === true ? (
								<span>{String(row.reason || 'Open Bid Leveling to choose the exact row.')}</span>
							) : (
								<span>
									{row.before === null || row.before === undefined || row.before === ''
										? 'Blank'
										: money(row.before)}{' '}
									→ {money(row.after)}
								</span>
							)}
							{typeof row.answerAfter === 'string' && row.answerAfter && (
								<small>Checklist answer after approval: {row.answerAfter}</small>
							)}
							{typeof row.evidence === 'string' && row.evidence && (
								<blockquote>{row.evidence}</blockquote>
							)}
						</li>
					))}
				</ul>
			</div>
		);
	}
	if (preview.kind === 'BIDDER_ACTION_ITEM_MESSAGE_BATCH') {
		return (
			<BidderActionItemMessageBatch preview={preview as BidderActionItemMessageBatchPreview} />
		);
	}
	if (preview.kind === 'GOVERNED_OPERATION_PLAN') {
		const changes = Array.isArray(preview.changes)
			? (preview.changes as AutopilotPreviewItem[])
			: [];
		return (
			<div className="autopilot-governed-operation-preview">
				<div className="autopilot-governed-operation-preview__context">
					{typeof preview.projectName === 'string' && <span>{preview.projectName}</span>}
					{typeof preview.packageName === 'string' && <span>{preview.packageName}</span>}
					{typeof preview.bidderName === 'string' && <span>{preview.bidderName}</span>}
				</div>
				{typeof preview.scopeText === 'string' && preview.scopeText.trim() && (
					<h4 className="autopilot-governed-operation-preview__scope">{preview.scopeText}</h4>
				)}
				<ul>
					{changes.map((change, index) => (
						<li key={String(change.id || change.field || index)}>
							<span>{String(change.label || change.field || 'Change')}</span>
							<div>
								<small>Current</small>
								<strong>
									{change.field === 'adjustmentCost' && Number.isFinite(Number(change.before))
										? Number(change.before).toLocaleString(undefined, {
												style: 'currency',
												currency: 'USD',
										  })
										: displayPreviewValue(change.before) || 'Not set'}
								</strong>
							</div>
							<div>
								<small>Proposed</small>
								<strong>
									{change.field === 'adjustmentCost' && Number.isFinite(Number(change.after))
										? Number(change.after).toLocaleString(undefined, {
												style: 'currency',
												currency: 'USD',
										  })
										: displayPreviewValue(change.after) || 'Clear value'}
								</strong>
							</div>
						</li>
					))}
				</ul>
				{typeof preview.reason === 'string' && preview.reason.trim() && (
					<p>
						<b>Reason</b>
						{preview.reason}
					</p>
				)}
			</div>
		);
	}
	if (preview.kind === 'NEEDS_INPUT') {
		const missingFields = Array.isArray(preview.missingFields)
			? preview.missingFields.map(String).filter(Boolean)
			: [];
		const fieldLabel = (value: string) =>
			value
				.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
				.replace(/[_-]+/g, ' ')
				.replace(/^./, (character) => character.toUpperCase());
		return (
			<div className="autopilot-structured-preview">
				<strong>Additional information is needed</strong>
				{typeof preview.request === 'string' && preview.request.trim() && <p>{preview.request}</p>}
				{missingFields.length > 0 && (
					<>
						<small>Missing or unresolved fields</small>
						<ul>
							{missingFields.map((field) => (
								<li key={field}>{fieldLabel(field)}</li>
							))}
						</ul>
					</>
				)}
				{typeof preview.safeReason === 'string' && preview.safeReason.trim() && (
					<p>{preview.safeReason}</p>
				)}
			</div>
		);
	}

	const primaryText = [preview.summary, preview.text, preview.message].find(
		(value) => typeof value === 'string' && value.trim(),
	) as string | undefined;
	const beforeText = hideComparison ? '' : displayPreviewValue(preview.before);
	const afterText = hideComparison ? '' : displayPreviewValue(preview.after);
	const hasBefore = Boolean(beforeText);
	const hasAfter = Boolean(afterText);
	const { entries } = previewEntries(preview);
	const hasItems = entries.length > 0;
	const skippedItems = Array.isArray(preview.skippedItems) ? preview.skippedItems : [];
	const hasRecipientSummary = Boolean(preview.recipientSummary?.recipients?.length);
	const hasSourceCoverage = Array.isArray(preview.sourceCoverage)
		? preview.sourceCoverage.length > 0
		: Boolean(preview.sourceCoverage);
	const hasFriendlyContent = Boolean(
		primaryText ||
			hasBefore ||
			hasAfter ||
			hasItems ||
			skippedItems.length ||
			hasRecipientSummary ||
			hasSourceCoverage,
	);

	if (!hasFriendlyContent) return null;

	return (
		<div className="autopilot-structured-preview">
			{primaryText && <p>{primaryText}</p>}
			<SourceCoverage preview={preview} />
			<RecipientSummary preview={preview} />
			{(hasBefore || hasAfter) && (
				<div
					className={`autopilot-structured-preview__comparison${
						hasBefore && hasAfter ? ' has-both' : ''
					}`}
				>
					{hasBefore && (
						<section aria-label="Before">
							<strong>Before</strong>
							<pre>{beforeText}</pre>
						</section>
					)}
					{hasAfter && (
						<section aria-label="After">
							<strong>After</strong>
							<pre>{afterText}</pre>
						</section>
					)}
				</div>
			)}
			{hasItems && (
				<ul className="autopilot-structured-preview__items">
					{entries.map((item, index) => {
						const label =
							preview.kind === 'QUOTE_ALTERNATE_BATCH'
								? quoteAlternateDescription(item, index)
								: previewItemLabel(item, index);
						const contact = preview.kind === 'BID_LIST_SELECTIONS' ? bidListContactLabel(item) : '';
						const packageName =
							preview.kind === 'BID_LIST_SELECTIONS' && typeof item.packageName === 'string'
								? item.packageName
								: '';
						const hasPreferredDisplayText = [item.displayText, item.requirementText].some(
							(value) => typeof value === 'string' && value.trim(),
						);
						const rawDescription =
							!hasPreferredDisplayText &&
							typeof item.description === 'string' &&
							item.description !== label
								? item.description
								: '';
						const cleanedDescription = cleanLegacyActionItemText(rawDescription);
						const description = cleanedDescription !== label ? cleanedDescription : '';
						const alternateDetail =
							preview.kind === 'QUOTE_ALTERNATE_BATCH' ? quoteAlternateDetail(item) : '';
						const status = typeof item.status === 'string' ? item.status : '';
						return (
							<li
								className={item.selected === false ? 'is-excluded' : ''}
								key={String(item.id === undefined ? index : item.id)}
							>
								<span className="autopilot-structured-preview__selection" aria-hidden="true">
									{item.selected === false ? '—' : <FaCheck />}
								</span>
								<div>
									<strong>{label}</strong>
									{contact && (
										<small className="autopilot-structured-preview__contact">{contact}</small>
									)}
									{packageName && (
										<small className="autopilot-structured-preview__package">{packageName}</small>
									)}
									{description && <small>{description}</small>}
									{alternateDetail && <small>{alternateDetail}</small>}
									{status && <small className="autopilot-bid-list-status">{status}</small>}
								</div>
							</li>
						);
					})}
				</ul>
			)}
			{skippedItems.length > 0 && (
				<div className="autopilot-bid-list-skipped" aria-label="Skipped companies">
					<strong>Skipped</strong>
					<ul>
						{skippedItems.map((item, index) => (
							<li key={`${String(item?.companyName || 'company')}-${index}`}>
								<strong>{String(item?.companyName || 'Company')}</strong>
								<span>{String(item?.reason || 'This company could not be resolved safely.')}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

const A1ChangeSelector: FC<{
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
	disabled?: boolean;
	editableQuoteDescriptions?: boolean;
	quoteAlternates?: boolean;
}> = ({
	preview,
	onChange,
	disabled = false,
	editableQuoteDescriptions = false,
	quoteAlternates = false,
}) => {
	const { entries, key } = previewEntries(preview);
	if (!key || !entries.length) return null;
	return (
		<fieldset className="autopilot-a1-selector" disabled={disabled}>
			<legend>
				{quoteAlternates ? 'Choose quote-derived items to add' : 'Choose changes to add'}
			</legend>
			<p>Selected changes will be added to this package’s Bid Leveling checklist.</p>
			{entries.map((item, index) => {
				const selected = item.selected !== false;
				const itemKey = String(item.id === undefined ? index : item.id);
				const label = quoteAlternates
					? quoteAlternateDescription(item, index)
					: a1PreviewItemLabel(item, index);
				const updateItem = (patch: Partial<AutopilotPreviewItem>) =>
					onChange({
						...preview,
						[key]: entries.map((candidate, candidateIndex) =>
							candidateIndex === index ? { ...candidate, ...patch } : candidate,
						),
					});
				if (quoteAlternates && editableQuoteDescriptions) {
					return (
						<div
							className={`autopilot-a1-selector__editable-row ${
								selected ? 'is-selected' : 'is-excluded'
							}`}
							key={itemKey}
						>
							<label className="autopilot-a1-selector__editable-toggle">
								<input
									type="checkbox"
									aria-label={`Include ${label}`}
									checked={selected}
									onChange={(event) => updateItem({ selected: event.target.checked })}
								/>
								<span className="autopilot-a1-selector__indicator" aria-hidden="true">
									{selected ? <FaCheck /> : <FaTimes />}
								</span>
							</label>
							<label className="autopilot-a1-selector__editable-copy">
								<span>Description</span>
								<textarea
									aria-label={`Alternate description ${index + 1}`}
									rows={2}
									value={String(item.approvedDescription ?? item.description ?? '')}
									onChange={(event) => updateItem({ approvedDescription: event.target.value })}
								/>
								{quoteAlternateDetail(item) && <small>{quoteAlternateDetail(item)}</small>}
							</label>
							<span className="sr-only">
								{selected ? 'Included in checklist' : 'Excluded from checklist'}
							</span>
						</div>
					);
				}
				return (
					<label className={selected ? 'is-selected' : 'is-excluded'} key={itemKey}>
						<input
							type="checkbox"
							disabled={disabled || item.disabled === true}
							aria-label={quoteAlternates ? `Include ${label}` : undefined}
							checked={selected}
							onChange={(event) => updateItem({ selected: event.target.checked })}
						/>
						<span className="autopilot-a1-selector__indicator" aria-hidden="true">
							{selected ? <FaCheck /> : <FaTimes />}
						</span>
						<span>
							{label}
							{quoteAlternates && quoteAlternateDetail(item) && (
								<small>{quoteAlternateDetail(item)}</small>
							)}
						</span>
						<span className="sr-only">
							{selected ? 'Included in checklist' : 'Excluded from checklist'}
						</span>
					</label>
				);
			})}
		</fieldset>
	);
};

const ScopeGapSelector: FC<{
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
	disabled?: boolean;
}> = ({ preview, onChange, disabled = false }) => {
	const { entries, key } = previewEntries(preview);
	if (!key || !entries.length) return null;
	return (
		<fieldset className="autopilot-a1-selector autopilot-scope-gap-selector" disabled={disabled}>
			<legend>Choose scope findings to add</legend>
			<p>
				Each finding shows the detected requirement and ScrubPlan’s suggested checklist addition.
			</p>
			{entries.map((item, index) => {
				const selected = item.selected !== false;
				const itemKey = String(item.id === undefined ? index : item.id);
				const missing = String(
					item.missingOrDifferingRequirementFound || item.description || item.title || '',
				).trim();
				const suggested = String(
					item.suggestedChecklistAddition || item.title || item.description || '',
				).trim();
				return (
					<label className={selected ? 'is-selected' : 'is-excluded'} key={itemKey}>
						<input
							type="checkbox"
							aria-label={`Include scope finding ${index + 1}`}
							checked={selected}
							onChange={(event) =>
								onChange({
									...preview,
									[key]: entries.map((candidate, candidateIndex) =>
										candidateIndex === index
											? { ...candidate, selected: event.target.checked }
											: candidate,
									),
								})
							}
						/>
						<span className="autopilot-a1-selector__indicator" aria-hidden="true">
							{selected ? <FaCheck /> : <FaTimes />}
						</span>
						<span className="autopilot-scope-gap-selector__content">
							<span>
								<strong>Missing or Differing Requirement Found</strong>
								<small>{missing || 'Review the cited scope requirement.'}</small>
							</span>
							<span>
								<strong>Suggested Checklist Addition</strong>
								<small>{suggested || 'Add the cited requirement to the checklist.'}</small>
							</span>
						</span>
						<span className="sr-only">
							{selected ? 'Included in checklist' : 'Excluded from checklist'}
						</span>
					</label>
				);
			})}
		</fieldset>
	);
};

const QuoteScopeInclusionSelector: FC<{
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
	disabled?: boolean;
}> = ({ preview, onChange, disabled = false }) => {
	const { entries, key } = previewEntries(preview);
	if (!key || !entries.length) return null;
	const update = (index: number, updates: Record<string, unknown>) =>
		onChange({
			...preview,
			[key]: entries.map((candidate, candidateIndex) =>
				candidateIndex === index ? { ...candidate, ...updates } : candidate,
			),
		});
	return (
		<fieldset
			className="autopilot-a1-selector autopilot-quote-inclusion-selector"
			disabled={disabled}
		>
			<legend>Choose quote-only inclusions to add</legend>
			<p>
				Grouping names and checklist wording may be refined before approval. Quote evidence is
				read-only.
			</p>
			{entries.map((item, index) => {
				const selected = item.selected !== false;
				const itemKey = String(item.id === undefined ? index : item.id);
				return (
					<div className={selected ? 'is-selected' : 'is-excluded'} key={itemKey}>
						<label className="autopilot-quote-inclusion-selector__selection">
							<input
								type="checkbox"
								aria-label={`Include quote-only scope item ${index + 1}`}
								checked={selected}
								onChange={(event) => update(index, { selected: event.target.checked })}
							/>
							<span className="autopilot-a1-selector__indicator" aria-hidden="true">
								{selected ? <FaCheck /> : <FaTimes />}
							</span>
							<strong>{previewItemLabel(item, index)}</strong>
						</label>
						<div className="autopilot-quote-inclusion-selector__fields">
							<label>
								<span>New grouping name</span>
								<input
									value={String(item.groupingName || '')}
									onChange={(event) => update(index, { groupingName: event.target.value })}
								/>
							</label>
							<label>
								<span>Checklist requirement</span>
								<textarea
									rows={2}
									value={String(item.checklistText || '')}
									onChange={(event) => update(index, { checklistText: event.target.value })}
								/>
							</label>
							<div className="autopilot-quote-inclusion-selector__evidence">
								<strong>Quote evidence</strong>
								<p>
									{String(
										item.evidence || item.notes || 'Affirmative inclusion documented in the quote.',
									)}
								</p>
								{item.quotedAmount && (
									<small>Quoted amount: {String(item.quotedAmount)} (evidence only)</small>
								)}
							</div>
						</div>
					</div>
				);
			})}
		</fieldset>
	);
};

const BidListSelectionSelector: FC<{
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
	disabled?: boolean;
}> = ({ preview, onChange, disabled = false }) => {
	const { entries, key } = previewEntries(preview);
	if (!key || !entries.length) return null;
	const skippedItems = Array.isArray(preview.skippedItems) ? preview.skippedItems : [];
	return (
		<fieldset className="autopilot-a1-selector" disabled={disabled}>
			<legend>Choose bidders to add and invite</legend>
			<p>Uncheck any available company you do not want ScrubPlan to add.</p>
			{entries.map((item, index) => {
				const selected = item.selected !== false;
				const itemDisabled = item.disabled === true;
				const itemKey = String(item.id === undefined ? index : item.id);
				const company = previewItemLabel(item, index);
				const contact = bidListContactLabel(item);
				const packageName = typeof item.packageName === 'string' ? item.packageName : '';
				const reason = typeof item.description === 'string' ? item.description : '';
				const status = typeof item.status === 'string' ? item.status : '';
				return (
					<label
						className={`${selected ? 'is-selected' : 'is-excluded'}${
							itemDisabled ? ' is-disabled' : ''
						}`}
						key={itemKey}
					>
						<input
							type="checkbox"
							checked={selected}
							aria-label={`Add ${company}`}
							disabled={itemDisabled}
							onChange={(event) =>
								onChange({
									...preview,
									[key]: entries.map((candidate, candidateIndex) =>
										candidateIndex === index
											? { ...candidate, selected: event.target.checked }
											: candidate,
									),
								})
							}
						/>
						<span className="autopilot-a1-selector__indicator" aria-hidden="true">
							{selected ? <FaCheck /> : <FaTimes />}
						</span>
						<span>
							<strong>{company}</strong>
							{contact && (
								<small className="autopilot-structured-preview__contact">{contact}</small>
							)}
							{packageName && (
								<small className="autopilot-structured-preview__package">{packageName}</small>
							)}
							{reason && <small>{reason}</small>}
							{status && <small className="autopilot-bid-list-status">{status}</small>}
						</span>
						<span className="sr-only">
							{itemDisabled ? status || 'Unavailable' : selected ? 'Selected' : 'Not selected'}
						</span>
					</label>
				);
			})}
			{skippedItems.length > 0 && (
				<div className="autopilot-bid-list-skipped" aria-label="Skipped companies">
					<strong>Skipped</strong>
					<ul>
						{skippedItems.map((item, index) => (
							<li key={`${String(item?.companyName || 'company')}-${index}`}>
								<strong>{String(item?.companyName || 'Company')}</strong>
								<span>{String(item?.reason || 'This company could not be resolved safely.')}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</fieldset>
	);
};

const InitialAutoAnalysisOptionsControl: FC<{
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
	disabled?: boolean;
}> = ({ preview, onChange, disabled = false }) => {
	const options = initialAutoAnalysisOptions(preview);
	const choices: Array<{
		key: InitialAutoAnalysisOptionKey;
		label: string;
		description: string;
	}> = [
		{
			key: 'acceptSuggestedTrades',
			label: 'Auto-accept trade selections',
			description: 'Accept ScrubPlan’s suggested trade assignments when selection finishes.',
		},
		{
			key: 'createBidLevelingChecklists',
			label: 'Auto-create Bid Leveling checklists',
			description: 'Create each selected trade’s checklist after its analysis is ready.',
		},
		{
			key: 'proceedWithBidListAgent',
			label: 'Run Bid List Agent',
			description: 'Continue into bidder research after checklists are created.',
		},
	];
	return (
		<fieldset className="autopilot-initial-analysis-options" disabled={disabled}>
			<legend>Choose what Auto-Analyze should complete</legend>
			{choices.map((choice) => {
				const checked = options[choice.key];
				return (
					<button
						aria-checked={checked}
						className={checked ? 'is-on' : ''}
						key={choice.key}
						onClick={() =>
							onChange({
								...preview,
								options: updateInitialAutoAnalysisOption(options, choice.key, !checked),
							})
						}
						role="switch"
						type="button"
					>
						<span className="autopilot-initial-analysis-options__switch" aria-hidden="true">
							<span />
						</span>
						<span>
							<strong>{choice.label}</strong>
							<small>{choice.description}</small>
						</span>
					</button>
				);
			})}
		</fieldset>
	);
};

const costPlacementSelectionIssue = (preview: AutopilotStructuredPreview): string | null => {
	const { entries } = previewEntries(preview);
	const options = Array.isArray(preview.targetOptions) ? preview.targetOptions : [];
	const optionKeys = new Set(
		options.map((option) => String(option.targetKey || '')).filter(Boolean),
	);
	const selected = entries.filter((row) => row.selected !== false);
	if (!selected.length) return 'Select at least one cost item.';
	const selectedTargets = new Set<string>();
	for (const row of selected) {
		if (String(row.targetKind || '') !== 'NEW_BIDDER_ALTERNATE') {
			const targetKey = String(row.targetKey || '');
			if (!targetKey || !optionKeys.has(targetKey)) {
				return 'Choose an exact Bid Leveling destination for every selected cost.';
			}
			if (selectedTargets.has(targetKey)) {
				return 'Each selected cost must use a different Bid Leveling destination.';
			}
			selectedTargets.add(targetKey);
		}
	}
	return null;
};

const BidderCostPlacementEditor: FC<{
	disabled?: boolean;
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
}> = ({ disabled = false, preview, onChange }) => {
	const { entries, key } = previewEntries(preview);
	const options = Array.isArray(preview.targetOptions) ? preview.targetOptions : [];
	const [openRow, setOpenRow] = useState<string | null>(null);
	const [activeIndexes, setActiveIndexes] = useState<Record<string, number>>({});
	if (!key) return null;
	const updateRow = (index: number, patch: Record<string, unknown>) =>
		onChange({
			...preview,
			[key]: entries.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
		});
	return (
		<fieldset className="autopilot-cost-placement-editor" disabled={disabled}>
			<legend>Edit cost destinations</legend>
			<p>Amounts and bidder evidence are read-only. Select an exact existing Bid Leveling line.</p>
			{entries.map((row, index) => {
				const rowId = String(row.id ?? index);
				const isNewAlternate = String(row.targetKind || '') === 'NEW_BIDDER_ALTERNATE';
				const selectedOption = options.find(
					(option) => String(option.targetKey || '') === String(row.targetKey || ''),
				);
				const query = String(
					row.targetSearchText === undefined
						? selectedOption?.label || ''
						: row.targetSearchText || '',
				);
				const queryTokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
				const matches = options.filter((option) => {
					const haystack = `${String(option.label || '')} ${String(
						option.grouping || '',
					)}`.toLowerCase();
					return queryTokens.every((token) => haystack.includes(token));
				});
				const listboxId = `cost-targets-${rowId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
				const activeIndex = Math.min(activeIndexes[rowId] || 0, Math.max(matches.length - 1, 0));
				const choose = (option: AutopilotPreviewItem) => {
					updateRow(index, {
						before: option.currentValue,
						destination: option.destinationType,
						disabled: false,
						selected: true,
						targetKey: String(option.targetKey || ''),
						targetSearchText: String(option.label || ''),
					});
					setOpenRow(null);
				};
				return (
					<div className="autopilot-cost-placement-editor__row" key={rowId}>
						<label className="autopilot-cost-placement-editor__selection">
							<input
								type="checkbox"
								checked={row.selected !== false}
								onChange={(event) => updateRow(index, { selected: event.target.checked })}
							/>
							<span>{String(row.requirementText || `Cost item ${index + 1}`)}</span>
						</label>
						<div className="autopilot-cost-placement-editor__facts">
							<strong>{displayPreviewValue(row.after)}</strong>
							<span>{String(row.destination || 'No destination selected')}</span>
							{typeof row.evidence === 'string' && row.evidence.trim() && (
								<blockquote>{row.evidence}</blockquote>
							)}
						</div>
						{!isNewAlternate && (
							<div className="autopilot-cost-placement-editor__combobox">
								<label htmlFor={`${listboxId}-input`}>Bid Leveling destination</label>
								<input
									aria-autocomplete="list"
									aria-controls={listboxId}
									aria-expanded={openRow === rowId}
									id={`${listboxId}-input`}
									role="combobox"
									value={query}
									onFocus={() => setOpenRow(rowId)}
									onChange={(event) => {
										updateRow(index, {
											disabled: false,
											selected: true,
											targetKey: undefined,
											targetSearchText: event.target.value,
										});
										setActiveIndexes((current) => ({ ...current, [rowId]: 0 }));
										setOpenRow(rowId);
									}}
									onKeyDown={(event) => {
										if (event.key === 'Escape') {
											setOpenRow(null);
											return;
										}
										if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
											event.preventDefault();
											const delta = event.key === 'ArrowDown' ? 1 : -1;
											setActiveIndexes((current) => ({
												...current,
												[rowId]: Math.max(
													0,
													Math.min(matches.length - 1, (current[rowId] || 0) + delta),
												),
											}));
											setOpenRow(rowId);
										}
										if (event.key === 'Enter' && openRow === rowId && matches[activeIndex]) {
											event.preventDefault();
											choose(matches[activeIndex]);
										}
									}}
								/>
								{openRow === rowId && (
									<ul id={listboxId} role="listbox">
										{matches.map((option, optionIndex) => (
											<li
												aria-selected={
													String(option.targetKey || '') === String(row.targetKey || '')
												}
												className={optionIndex === activeIndex ? 'is-active' : ''}
												key={String(option.targetKey)}
												role="option"
											>
												<button
													type="button"
													onMouseDown={(event) => event.preventDefault()}
													onClick={() => choose(option)}
												>
													<strong>{String(option.label || 'Bid Leveling line')}</strong>
													<small>{String(option.grouping || option.destinationType || '')}</small>
												</button>
											</li>
										))}
										{matches.length === 0 && <li>No matching existing lines</li>}
									</ul>
								)}
							</div>
						)}
					</div>
				);
			})}
		</fieldset>
	);
};

const StructuredPreviewEditor: FC<{
	disabled?: boolean;
	deliveryReadOnly?: boolean;
	preview: AutopilotStructuredPreview;
	onChange: (preview: AutopilotStructuredPreview) => void;
}> = ({ disabled = false, deliveryReadOnly = false, preview, onChange }) => {
	if (
		preview.kind === 'BID_LEVELING_COST_PLACEMENT_BATCH' &&
		Number(preview.snapshotVersion || 0) >= 3
	) {
		return <BidderCostPlacementEditor disabled={disabled} onChange={onChange} preview={preview} />;
	}
	if (preview.kind === 'BIDDER_ACTION_ITEM_MESSAGE_BATCH') {
		return (
			<BidderActionItemMessageBatch
				disabled={disabled}
				editable
				onChange={onChange}
				preview={preview as BidderActionItemMessageBatchPreview}
			/>
		);
	}
	if (preview.kind === 'BIDDER_MESSAGE') {
		const message =
			preview.after && typeof preview.after === 'object' && !Array.isArray(preview.after)
				? (preview.after as Record<string, unknown>)
				: {};
		const channels = Array.isArray(message.channels) ? message.channels : [];
		const isInternalTeamDraft = channels.map(String).includes('TEAM_ONLY');
		const sourceQuestion =
			preview.before && typeof preview.before === 'object' && !Array.isArray(preview.before)
				? (preview.before as Record<string, unknown>)
				: null;
		const audienceOptions = Array.isArray(message.audienceOptions)
			? (message.audienceOptions as Array<Record<string, unknown>>)
			: [];
		const audienceValue =
			String(message.audience || '').toUpperCase() === 'PROJECT'
				? 'PROJECT'
				: `PACKAGE:${String(message.audienceBidPackageId || '')}`;
		const { recipientSummary } = preview;
		const recipients = Array.isArray(recipientSummary && recipientSummary.recipients)
			? recipientSummary!.recipients!
			: [];
		const excluded = Array.isArray(recipientSummary && recipientSummary.excluded)
			? recipientSummary!.excluded!
			: [];
		return (
			<div className="autopilot-action__message-editor">
				{isInternalTeamDraft && (
					<p className="autopilot-bidder-message-preview__internal-note">
						Internal project-team draft — nothing is sent to the bidder
					</p>
				)}
				{sourceQuestion && typeof sourceQuestion.body === 'string' && sourceQuestion.body.trim() && (
					<section
						className="autopilot-bidder-message-preview__question"
						aria-label="Question asked"
					>
						<span>Question asked</span>
						{typeof sourceQuestion.subject === 'string' && sourceQuestion.subject.trim() && (
							<strong>{sourceQuestion.subject}</strong>
						)}
						<p>{sourceQuestion.body}</p>
						{typeof sourceQuestion.channel === 'string' && sourceQuestion.channel.trim() && (
							<small>{sourceQuestion.channel}</small>
						)}
					</section>
				)}
				<SourceCoverage preview={preview} />
				{!deliveryReadOnly &&
					recipients.some((recipient) => typeof recipient.selected === 'boolean') && (
						<fieldset className="autopilot-action__batch-editor">
							<legend>Recipients</legend>
							<p>Select the package contacts who should receive this private request.</p>
							{recipients.map((recipient, index) => (
								<label
									key={`${String(recipient.invitationId || index)}:${String(
										recipient.vendorContactId || index,
									)}`}
								>
									<input
										type="checkbox"
										checked={recipient.selected !== false}
										onChange={(event) =>
											onChange({
												...preview,
												recipientSummary: {
													...preview.recipientSummary,
													recipients: recipients.map((candidate, candidateIndex) =>
														candidateIndex === index
															? { ...candidate, selected: event.target.checked }
															: candidate,
													),
												},
											})
										}
									/>
									<span>
										<strong>{recipient.companyName || 'Bidder'}</strong>
										<small>
											{[recipient.contactName, recipient.email, recipient.status]
												.filter(Boolean)
												.join(' · ')}
										</small>
									</span>
								</label>
							))}
						</fieldset>
					)}
				{audienceOptions.length > 0 && (
					<label>
						<span>Audience</span>
						<select
							disabled={disabled || deliveryReadOnly}
							value={audienceValue}
							onChange={(event) => {
								const { value } = event.target;
								onChange({
									...preview,
									after: {
										...message,
										audience: value === 'PROJECT' ? 'PROJECT' : 'PACKAGE',
										audienceBidPackageId:
											value === 'PROJECT' ? null : Number(value.replace('PACKAGE:', '')),
									},
								});
							}}
						>
							{audienceOptions.map((option, index) => {
								const value =
									String(option.value || '').toUpperCase() === 'PROJECT'
										? 'PROJECT'
										: `PACKAGE:${String(option.bidPackageId || '')}`;
								return (
									<option key={`${value}:${index}`} value={value}>
										{String(option.label || 'Bid package')}
									</option>
								);
							})}
						</select>
					</label>
				)}
				<label>
					<span>Subject</span>
					<input
						aria-label="Subject"
						disabled={disabled}
						value={String(message.subject || '')}
						onChange={(event) =>
							onChange({ ...preview, after: { ...message, subject: event.target.value } })
						}
					/>
				</label>
				<label>
					<span>{isInternalTeamDraft ? 'Internal draft' : 'Message'}</span>
					<textarea
						aria-label="Message"
						disabled={disabled}
						rows={7}
						value={String(message.body || '')}
						onChange={(event) =>
							onChange({ ...preview, after: { ...message, body: event.target.value } })
						}
					/>
				</label>
				{channels.length > 0 && (
					<div
						className="autopilot-bidder-message-preview__channels"
						aria-label="Delivery channels"
					>
						{channels.map((channel) => (
							<small key={String(channel)}>{String(channel)}</small>
						))}
					</div>
				)}
				{deliveryReadOnly && <RecipientSummary preview={preview} />}
				{!deliveryReadOnly && excluded.length > 0 && (
					<div className="autopilot-action__recipient-exclusions">
						<strong>Excluded</strong>
						<ul>
							{excluded.map((recipient, index) => (
								<li
									key={`${String(recipient.invitationId ?? '')}:${String(
										recipient.vendorContactId ?? recipient.email ?? index,
									)}`}
								>
									{recipient.companyName || recipient.contactName || recipient.email || 'Bidder'}
									{recipient.companyName && recipient.contactName
										? ` · ${recipient.contactName}`
										: ''}
									{recipient.reason ? ` — ${recipient.reason}` : ''}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	}
	const { entries, key } = previewEntries(preview);
	const selectableEntries = entries.filter((item) => typeof item.selected === 'boolean');
	return (
		<fieldset className="autopilot-action__batch-editor">
			<legend>Edit before approving</legend>
			<p>Choose the items ScrubPlan should add.</p>
			{selectableEntries.map((item) => {
				const index = entries.indexOf(item);
				const itemKey = String(item.id === undefined ? index : item.id);
				const label = previewItemLabel(item, index);
				const contact = preview.kind === 'BID_LIST_SELECTIONS' ? bidListContactLabel(item) : '';
				const packageName =
					preview.kind === 'BID_LIST_SELECTIONS' && typeof item.packageName === 'string'
						? item.packageName
						: '';
				const description =
					typeof item.description === 'string' && item.description !== label
						? item.description
						: '';
				return (
					<label key={itemKey}>
						<input
							type="checkbox"
							disabled={disabled || item.disabled === true}
							checked={item.selected !== false}
							onChange={(event) =>
								onChange({
									...preview,
									...(key
										? {
												[key]: entries.map((candidate, candidateIndex) =>
													candidateIndex === index
														? { ...candidate, selected: event.target.checked }
														: candidate,
												),
										  }
										: {}),
								})
							}
						/>
						<span>
							<strong>{label}</strong>
							{contact && (
								<small className="autopilot-structured-preview__contact">{contact}</small>
							)}
							{packageName && (
								<small className="autopilot-structured-preview__package">{packageName}</small>
							)}
							{description && <small>{description}</small>}
						</span>
					</label>
				);
			})}
		</fieldset>
	);
};

const executionProgressFor = (action: AutopilotAction) => {
	const result =
		action.executionResult && typeof action.executionResult === 'object'
			? (action.executionResult as Record<string, unknown>)
			: {};
	const resultRuns = Array.isArray(result.linkedRuns)
		? (result.linkedRuns as Array<Record<string, unknown>>)
		: [];
	const actionRuns = Array.isArray(action.linkedRuns) ? action.linkedRuns : [];
	const firstRun = (actionRuns[0] || resultRuns[0] || {}) as Record<string, unknown>;
	const rawStage =
		action.executionStage ||
		(typeof result.currentStage === 'string' ? result.currentStage : '') ||
		(typeof result.stage === 'string' ? result.stage : '') ||
		(typeof firstRun.stage === 'string' ? firstRun.stage : '') ||
		(typeof result.waitingOn === 'string' ? result.waitingOn : '');
	const rawStatus =
		(typeof firstRun.status === 'string' ? firstRun.status : '') ||
		(typeof result.status === 'string' ? result.status : '') ||
		action.status;
	const isChangeLogAction = action.type === 'A1_START_CHANGE_ANALYSIS';
	const isRunning = ['APPROVED', 'ANALYZING', 'EXECUTING'].includes(action.status);
	const message =
		action.executionMessage ||
		(typeof result.message === 'string' ? result.message : '') ||
		(typeof firstRun.message === 'string' ? firstRun.message : '') ||
		(isChangeLogAction && isRunning
			? result.adoptedExistingRun === true
				? 'The existing Change Log run was adopted and is still processing.'
				: 'The Change Log run is still processing.'
			: '');
	return {
		message,
		stage: rawStage ? statusLabel(rawStage) : isChangeLogAction ? 'Change Log analysis' : '',
		status: rawStatus ? statusLabel(rawStatus) : '',
	};
};

export const quoteAlternateLifecycleMessage = (action: AutopilotAction): string | null => {
	if (action.type !== 'A8_ADD_MISSING_QUOTE_ALTERNATES') return null;
	const result =
		action.executionResult && typeof action.executionResult === 'object'
			? (action.executionResult as Record<string, unknown>)
			: {};
	const alternateItemIds = Array.isArray(result.alternateItemIds)
		? result.alternateItemIds.filter(Boolean)
		: [];
	const messageIds = Array.isArray(result.messageIds) ? result.messageIds.filter(Boolean) : [];
	const deliveryIds = Array.isArray(result.deliveryIds) ? result.deliveryIds.filter(Boolean) : [];
	if (action.status === 'FAILED') {
		return alternateItemIds.length > 0
			? 'The selected alternate was added, but its bidder notification needs attention. Retrying will reuse the existing message and will not add another alternate.'
			: 'Nothing was committed because validation failed before the alternate or bidder notification was queued.';
	}
	if (
		['EXECUTING', 'COMPLETED'].includes(action.status) &&
		alternateItemIds.length > 0 &&
		(messageIds.length > 0 || deliveryIds.length > 0)
	) {
		return 'The selected alternate was added and its bidder notification was queued successfully.';
	}
	return null;
};

const ExecutionProgress: FC<{ action: AutopilotAction }> = ({ action }) => {
	const progress = executionProgressFor(action);
	const running = ['APPROVED', 'ANALYZING', 'EXECUTING'].includes(action.status);
	if (!running && !progress.message && !progress.stage) return null;
	return (
		<div
			className={`autopilot-action__execution${running ? ' is-running' : ''}`}
			role={running ? 'status' : undefined}
			aria-live={running ? 'polite' : undefined}
		>
			<span aria-hidden="true">
				{running ? <FaSpinner className="autopilot-spin" /> : <FaCheckCircle />}
			</span>
			<div>
				<strong>{running ? 'Work is in progress' : 'Execution result'}</strong>
				<small>
					{[progress.stage, progress.status].filter(Boolean).join(' · ') ||
						'ScrubPlan is confirming the workflow status.'}
				</small>
				{progress.message && <p>{progress.message}</p>}
			</div>
		</div>
	);
};

export const AutopilotActionCard: FC<{
	action: AutopilotAction;
	directTextEditing?: boolean;
	initialEditing?: boolean;
}> = ({ action, directTextEditing = true, initialEditing = false }) => {
	const {
		actionBusy,
		actionErrors,
		consumeActionFocus,
		focusActionId,
		markActionSeen,
		registerCardDraft,
		refresh,
		runActionCommand,
	} = useAutopilot();
	const key = String(action.id);
	const cardRef = useRef<HTMLElement>(null);
	const busy = Boolean(actionBusy[key]);
	const [expanded, setExpanded] = useState(
		action.status === 'PENDING_APPROVAL' ||
			(action.status === 'COMPLETED' && action.modeSnapshot === 'SELF_DRIVING'),
	);
	const [editing, setEditing] = useState(initialEditing);
	const [bidInviteWizardOpen, setBidInviteWizardOpen] = useState(false);
	const [editedPreview, setEditedPreview] = useState<AutopilotActionPreview>(() =>
		editablePreviewForAction(action),
	);

	useEffect(() => {
		setEditedPreview(editablePreviewForAction(action));
		setEditing(false);
		// Preserve in-progress selections until the server action version changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [action.id, action.version]);

	useEffect(() => {
		markActionSeen(action);
	}, [action, markActionSeen]);

	useEffect(() => {
		const previewChanged =
			JSON.stringify(editedPreview) !== JSON.stringify(editablePreviewForAction(action));
		registerCardDraft(action.id, editing || previewChanged);
		return () => registerCardDraft(action.id, false);
	}, [action, editedPreview, editing, registerCardDraft]);

	useEffect(() => {
		if (focusActionId !== key) return;
		cardRef.current?.focus();
		consumeActionFocus(action.id);
	}, [action.id, consumeActionFocus, focusActionId, key]);

	const isA1ChangeBatch =
		action.type === 'A1_APPLY_PACKAGE_CHANGES' && isStructuredPreview(editedPreview);
	const isInitialAutoAnalysis =
		action.type === 'A1_START_INITIAL_AUTO_ANALYSIS' && isStructuredPreview(editedPreview);
	const isBidListSelection =
		(action.type === 'A5_APPLY_REPLACEMENT_BIDDERS' || action.type === 'ADD_PACKAGE_BIDDER') &&
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'BID_LIST_SELECTIONS';
	const isChatBidListSelection = isBidListSelection && action.type === 'ADD_PACKAGE_BIDDER';
	const hasSelectedChatBidder =
		!isChatBidListSelection ||
		previewEntries(editedPreview as AutopilotStructuredPreview).entries.some(
			(item) => item.disabled !== true && item.selected !== false,
		);
	const isInitialBidInvitation = action.type === 'A5_SEND_INITIAL_BID_INVITES';
	const isQuoteAlternateBatch =
		action.type.indexOf('A8_') === 0 &&
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'QUOTE_ALTERNATE_BATCH';
	const isQuoteScopeInclusionBatch =
		action.type === 'A8_ADD_QUOTE_SCOPE_INCLUSIONS' &&
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'QUOTE_SCOPE_INCLUSION_BATCH';
	const isBidderCostPlacementBatch =
		action.type === 'A4_APPLY_BIDDER_MESSAGE_COST_UPDATES' &&
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'BID_LEVELING_COST_PLACEMENT_BATCH';
	const isEditableBidderCostPlacement =
		isBidderCostPlacementBatch &&
		Number((editedPreview as AutopilotStructuredPreview).snapshotVersion || 0) >= 3;
	const bidderCostPlacementError = isEditableBidderCostPlacement
		? costPlacementSelectionIssue(editedPreview as AutopilotStructuredPreview)
		: null;
	const hasSelectedBidderCostPlacement =
		!isBidderCostPlacementBatch ||
		(isEditableBidderCostPlacement
			? !bidderCostPlacementError
			: previewEntries(editedPreview as AutopilotStructuredPreview).entries.some(
					(item) => item.disabled !== true && item.selected !== false,
			  ));
	const isScopeGapBatch =
		action.type === 'A2_APPLY_SCOPE_GAP_BATCH' && isStructuredPreview(editedPreview);
	const isBidderActionItemMessageBatch =
		isStructuredPreview(editedPreview) && editedPreview.kind === 'BIDDER_ACTION_ITEM_MESSAGE_BATCH';
	const isDirectBidClarification =
		directTextEditing &&
		action.status === 'PENDING_APPROVAL' &&
		action.type === 'A4_SEND_BIDDER_ACTION_ITEMS' &&
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'BIDDER_MESSAGE';
	const isInternalA7TeamDraft = action.type === 'A7_CREATE_TEAM_REVIEW_DRAFT';
	const bidderActionItemMessageBatchError = isBidderActionItemMessageBatch
		? bidderActionItemMessageBatchIssue(editedPreview as BidderActionItemMessageBatchPreview)
		: null;
	const selectableBidderMessageRecipients =
		isStructuredPreview(editedPreview) &&
		editedPreview.kind === 'BIDDER_MESSAGE' &&
		editedPreview.recipientSelectionEditable === true
			? (editedPreview.recipientSummary && editedPreview.recipientSummary.recipients) || []
			: null;
	const isSelectableBidderMessage = selectableBidderMessageRecipients !== null;
	const hasSelectedBidderMessageRecipient =
		!isSelectableBidderMessage ||
		Boolean(selectableBidderMessageRecipients!.some((recipient) => recipient.selected !== false));
	const quoteAlternateOutcome = quoteAlternateLifecycleMessage(action);

	const approve = async () => {
		if (isInitialBidInvitation && action.projectId) {
			setBidInviteWizardOpen(true);
			return;
		}
		const previewChanged =
			JSON.stringify(editedPreview) !== JSON.stringify(editablePreviewForAction(action));
		const approvalPreview =
			isQuoteScopeInclusionBatch && isStructuredPreview(editedPreview)
				? quoteScopeInclusionSelectionPayload(editedPreview)
				: isQuoteAlternateBatch && isStructuredPreview(editedPreview)
				? quoteAlternateSelectionPayload(editedPreview)
				: isChatBidListSelection && isStructuredPreview(editedPreview)
				? packageBidderSelectionPayload(editedPreview)
				: isBidderCostPlacementBatch && isStructuredPreview(editedPreview)
				? bidderCostPlacementSelectionPayload(editedPreview)
				: isBidderActionItemMessageBatch && isStructuredPreview(editedPreview)
				? bidderActionItemMessageBatchApprovalPayload(
						editedPreview as BidderActionItemMessageBatchPreview,
				  )
				: editedPreview;
		const success = await runActionCommand(
			action,
			'approve',
			isA1ChangeBatch ||
				isInitialAutoAnalysis ||
				isChatBidListSelection ||
				isQuoteAlternateBatch ||
				isQuoteScopeInclusionBatch ||
				isBidderCostPlacementBatch ||
				isScopeGapBatch ||
				isBidderActionItemMessageBatch ||
				previewChanged
				? approvalPreview
				: undefined,
		);
		if (success) setEditing(false);
	};

	const canDecide = action.status === 'PENDING_APPROVAL';
	const canRetry = action.status === 'FAILED' && action.retryAvailable === true;
	// FAILED actions are dismissible even when the Web bundle is temporarily
	// paired with an older API payload that predates acknowledgementAvailable.
	const canDismissTerminal = action.acknowledgementAvailable === true || action.status === 'FAILED';
	const canReviewClarificationInputs =
		action.type === 'A17_REQUEST_REQUIRED_INPUT' &&
		action.triggerType === 'BIDDER_CLARIFICATION_DRAFT_INVALID' &&
		Boolean(action.navigation?.projectId && action.navigation?.bidPackageId);
	const canEditPreview =
		typeof action.preview === 'string' ||
		(isStructuredPreview(action.preview) &&
			(action.preview.kind === 'BIDDER_MESSAGE' ||
				action.preview.kind === 'BIDDER_ACTION_ITEM_MESSAGE_BATCH' ||
				previewEntries(action.preview).entries.some((item) => typeof item.selected === 'boolean')));
	const isSelfDrivingCompletion =
		action.status === 'COMPLETED' && action.modeSnapshot === 'SELF_DRIVING';
	const hasDetails = Boolean(
		action.reason ||
			action.impact ||
			action.preview ||
			action.citations?.length ||
			isSelfDrivingCompletion ||
			action.executionResult ||
			action.executionMessage ||
			action.executionStage,
	);

	const publishInitialInvitations = async (options: PublishProjectOptions) => {
		const basePreview = isStructuredPreview(action.preview) ? action.preview : {};
		const success = await runActionCommand(action, 'approve', {
			...basePreview,
			kind: basePreview.kind || 'BID_INVITE_PUBLISH',
			publishOptions: options,
		});
		if (!success) throw new Error('The invitations could not be published from this action.');
	};

	return (
		<>
			<section
				ref={cardRef}
				tabIndex={-1}
				data-autopilot-action-id={key}
				className={`autopilot-action autopilot-action--${action.status.toLowerCase()}`}
				aria-labelledby={`autopilot-action-title-${key}`}
			>
				<div className="autopilot-action__topline">
					<span className="autopilot-action__status">
						{action.status === 'PENDING_APPROVAL' ? 'Needs approval' : statusLabel(action.status)}
					</span>
					{action.risk && (
						<span
							className={`autopilot-action__risk autopilot-action__risk--${action.risk.toLowerCase()}`}
						>
							{action.risk.toLowerCase()} risk
						</span>
					)}
					{action.stepLabel && <span className="autopilot-action__step">{action.stepLabel}</span>}
				</div>
				<h3 id={`autopilot-action-title-${key}`}>{actionDisplayTitle(action)}</h3>
				{action.summary && <p className="autopilot-action__summary">{action.summary}</p>}
				<div className="autopilot-action__meta">
					{action.projectName && <span>{action.projectName}</span>}
					{action.bidPackageName && <span>{action.bidPackageName}</span>}
					{action.bidderName && <span>Bidder: {action.bidderName}</span>}
					{action.triggerLabel && <span>Trigger: {action.triggerLabel}</span>}
					{(action.urgency || action.risk) && (
						<span>Urgency: {(action.urgency || action.risk)?.toLowerCase()}</span>
					)}
					<time dateTime={action.createdAt}>{formatTime(action.createdAt)}</time>
					{typeof action.confidence === 'number' && (
						<span>
							{Math.round(action.confidence * (action.confidence <= 1 ? 100 : 1))}% confidence
						</span>
					)}
				</div>
				<ExecutionProgress action={action} />

				{hasDetails && (
					<button
						className="autopilot-action__details-toggle"
						type="button"
						onClick={() => setExpanded((current) => !current)}
						aria-expanded={expanded}
					>
						{expanded ? <FaChevronUp /> : <FaChevronDown />}
						{expanded ? 'Hide details' : 'Review details'}
					</button>
				)}

				{expanded && (
					<div className="autopilot-action__details">
						{(action.reason || action.impact || action.preview) && (
							<div className="autopilot-action__explanation">
								<strong>Why ScrubPlan suggests this and proposed change</strong>
								{action.reason && <p>{action.reason}</p>}
								{action.impact && <p>{action.impact}</p>}
								{isQuoteScopeInclusionBatch ? (
									<QuoteScopeInclusionSelector
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
										disabled={busy || !canDecide}
									/>
								) : isA1ChangeBatch || isQuoteAlternateBatch ? (
									<A1ChangeSelector
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
										disabled={busy || !canDecide}
										editableQuoteDescriptions={
											isQuoteAlternateBatch && directTextEditing && canDecide
										}
										quoteAlternates={isQuoteAlternateBatch}
									/>
								) : isScopeGapBatch ? (
									<ScopeGapSelector
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
										disabled={busy || !canDecide}
									/>
								) : isBidListSelection ? (
									<BidListSelectionSelector
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
										disabled={busy || !canDecide}
									/>
								) : isBidderActionItemMessageBatch ? (
									<BidderActionItemMessageBatch
										disabled={busy || !canDecide}
										editable
										onChange={setEditedPreview}
										preview={editedPreview as BidderActionItemMessageBatchPreview}
									/>
								) : isDirectBidClarification ? (
									<StructuredPreviewEditor
										disabled={busy || !canDecide}
										deliveryReadOnly
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
									/>
								) : (
									action.preview &&
									!editing && (
										<Preview
											preview={action.preview}
											hideComparison={action.type === 'A1_START_INITIAL_AUTO_ANALYSIS'}
										/>
									)
								)}
								{isInitialAutoAnalysis && (
									<InitialAutoAnalysisOptionsControl
										preview={editedPreview as AutopilotStructuredPreview}
										onChange={setEditedPreview}
										disabled={busy || !canDecide}
									/>
								)}
							</div>
						)}
						{isSelfDrivingCompletion && (
							<div
								className="autopilot-action__decision"
								aria-label="Self-driving decision details"
							>
								<strong>Self-driving decision</strong>
								{action.decision && <p>Decision: {statusLabel(action.decision)}</p>}
								{action.decisionReason && <p>Reasoning: {action.decisionReason}</p>}
								{action.executionResult !== undefined && action.executionResult !== null && (
									<div>
										<span>Result</span>
										{typeof action.executionResult === 'object' ? (
											<Preview preview={action.executionResult as AutopilotStructuredPreview} />
										) : (
											<p>{displayPreviewValue(action.executionResult)}</p>
										)}
									</div>
								)}
								{action.resolvedByName && <small>Resolved by {action.resolvedByName}</small>}
							</div>
						)}

						{editing && typeof editedPreview === 'string' && (
							<label className="autopilot-action__editor">
								<span>Edit before approving</span>
								<textarea
									autoFocus
									value={editedPreview}
									onChange={(event) => setEditedPreview(event.target.value)}
									rows={6}
								/>
							</label>
						)}
						{editing &&
							isStructuredPreview(editedPreview) &&
							!isBidderActionItemMessageBatch &&
							!isDirectBidClarification && (
								<StructuredPreviewEditor
									disabled={busy || !canDecide}
									deliveryReadOnly={action.type === 'A4_SEND_BIDDER_ACTION_ITEMS'}
									preview={editedPreview}
									onChange={setEditedPreview}
								/>
							)}
						<Citations citations={action.citations} />
					</div>
				)}

				{(action.blockedReason || action.errorMessage || actionErrors[key]) && (
					<div className="autopilot-action__error" role="alert">
						<FaShieldAlt aria-hidden="true" />
						<span>{actionErrors[key] || action.blockedReason || action.errorMessage}</span>
					</div>
				)}
				{editing && bidderCostPlacementError && (
					<div className="autopilot-action__error" role="alert">
						<FaShieldAlt aria-hidden="true" />
						<span>{bidderCostPlacementError}</span>
					</div>
				)}
				{quoteAlternateOutcome && (
					<div className="autopilot-action__execution" role="status" aria-live="polite">
						{action.status === 'FAILED' ? (
							<FaShieldAlt aria-hidden="true" />
						) : (
							<FaCheckCircle aria-hidden="true" />
						)}
						<div>
							<strong>Alternate workflow status</strong>
							<p>{quoteAlternateOutcome}</p>
						</div>
					</div>
				)}

				<div className="autopilot-action__buttons">
					{canReviewClarificationInputs && (
						<button
							className="autopilot-button"
							type="button"
							onClick={() => {
								const target = `/project/${Number(
									action.navigation?.projectId,
								)}/bidPackageData/${Number(
									action.navigation?.bidPackageId,
								)}?tab=bid-leveling&actionItemRunId=${Number(
									action.navigation?.actionItemRunId || 0,
								)}`;
								window.location.assign(target);
							}}
						>
							<FaArrowRight /> Review inputs
						</button>
					)}
					{canDecide && (
						<>
							<button
								className="autopilot-button autopilot-button--primary"
								type="button"
								onClick={approve}
								disabled={
									busy ||
									Boolean(bidderActionItemMessageBatchError) ||
									!hasSelectedChatBidder ||
									!hasSelectedBidderCostPlacement ||
									!hasSelectedBidderMessageRecipient
								}
							>
								{busy ? <FaSpinner className="autopilot-spin" /> : <FaCheck />}
								{isInternalA7TeamDraft
									? editing
										? 'Create edited team draft'
										: 'Create team draft'
									: editing
									? 'Approve edited'
									: 'Accept'}
							</button>
							{canEditPreview &&
								!isA1ChangeBatch &&
								!isInitialAutoAnalysis &&
								!isBidListSelection &&
								!isQuoteAlternateBatch &&
								!isBidderActionItemMessageBatch &&
								!isDirectBidClarification && (
									<button
										className="autopilot-button"
										type="button"
										onClick={() => {
											setExpanded(true);
											setEditing((current) => !current);
										}}
										disabled={busy || !action.preview || !canEditPreview}
										title={
											isBidderCostPlacementBatch
												? isEditableBidderCostPlacement
													? 'Edit cost destinations before approval'
													: 'Choose cost items before approval'
												: 'Edit this proposal before approval'
										}
									>
										<FaPen />{' '}
										{isBidderCostPlacementBatch
											? isEditableBidderCostPlacement
												? 'Edit destinations'
												: 'Choose items'
											: 'Edit'}
									</button>
								)}
							<button
								className="autopilot-button autopilot-button--quiet"
								type="button"
								onClick={() => runActionCommand(action, 'dismiss')}
								disabled={busy}
							>
								<FaBan /> Dismiss
							</button>
						</>
					)}
					{canRetry && (
						<button
							className="autopilot-button autopilot-button--primary"
							type="button"
							onClick={() => runActionCommand(action, 'retry')}
							disabled={busy}
						>
							{busy ? <FaSpinner className="autopilot-spin" /> : <FaRedo />} Retry
						</button>
					)}
					{canDismissTerminal && (
						<button
							className="autopilot-button"
							type="button"
							onClick={() => runActionCommand(action, 'dismiss')}
							disabled={busy}
						>
							<FaBan /> Dismiss
						</button>
					)}
					{action.status === 'COMPLETED' && action.undoAvailable && (
						<button
							className="autopilot-button"
							type="button"
							onClick={() => runActionCommand(action, 'undo')}
							disabled={busy}
						>
							<FaUndo /> Undo
						</button>
					)}
				</div>
			</section>
			{bidInviteWizardOpen && action.projectId && (
				<PublishProjectModal
					onBidListAuditCompleted={refresh}
					onClose={() => setBidInviteWizardOpen(false)}
					onPublish={publishInitialInvitations}
					projectId={action.projectId}
					projectName={action.projectName || 'this project'}
				/>
			)}
		</>
	);
};

const ActionCardStack: FC<{
	actions: AutopilotAction[];
	directTextEditing: boolean;
	label: string;
}> = ({ actions, directTextEditing, label }) => {
	const [index, setIndex] = useState(0);
	useEffect(() => {
		setIndex((current) => Math.max(0, Math.min(current, actions.length - 1)));
	}, [actions.length]);
	if (!actions.length) return null;
	// A successful decision removes the current card before the effect above can
	// clamp the previous index. Resolve the visible index synchronously so the
	// transient render can never pass an undefined action to the card.
	const visibleIndex = Math.max(0, Math.min(index, actions.length - 1));
	const action = actions[visibleIndex];
	if (!action) return null;
	return (
		<div className={`autopilot-action-stack${actions.length > 1 ? ' has-multiple' : ''}`}>
			<div className="autopilot-action-stack__navigation" aria-label={`${label} navigation`}>
				<span aria-live="polite">
					{visibleIndex + 1} of {actions.length}
				</span>
				{actions.length > 1 && (
					<div>
						<button
							type="button"
							aria-label={`Previous ${label.toLowerCase()} card`}
							disabled={visibleIndex === 0}
							onClick={() => setIndex((current) => Math.max(0, current - 1))}
						>
							<FaChevronUp />
						</button>
						<button
							type="button"
							aria-label={`Next ${label.toLowerCase()} card`}
							disabled={visibleIndex >= actions.length - 1}
							onClick={() => setIndex((current) => Math.min(actions.length - 1, current + 1))}
						>
							<FaChevronDown />
						</button>
					</div>
				)}
			</div>
			<AutopilotActionCard
				action={action}
				directTextEditing={directTextEditing}
				key={
					action.type === 'A4_SEND_BIDDER_ACTION_ITEMS'
						? `${String(action.id)}:${Number(action.version || 0)}`
						: String(action.id)
				}
			/>
		</div>
	);
};

const RecentActionItem: FC<{
	action: AutopilotAction;
	expanded: boolean;
	onToggle: () => void;
}> = ({ action, expanded, onToggle }) => {
	const accepted = action.decision === 'APPROVE';
	const contentId = `autopilot-recent-action-${String(action.id)}`;
	const description = action.description || action.summary || '';
	const details = [
		{ label: 'Description', value: description },
		{ label: 'Reason', value: action.reason },
		{ label: 'Impact', value: action.impact },
		{ label: 'Outcome', value: action.errorMessage },
	].filter(
		(detail, index, all) =>
			detail.value && all.findIndex((candidate) => candidate.value === detail.value) === index,
	);
	return (
		<article className="autopilot-recent-action" role="listitem">
			<button
				aria-controls={contentId}
				aria-expanded={expanded}
				className="autopilot-recent-action__disclosure"
				onClick={onToggle}
				type="button"
			>
				<div className="autopilot-recent-action__summary">
					<h3>{actionDisplayTitle(action)}</h3>
					<div className="autopilot-recent-action__meta">
						{action.projectName && <span>{action.projectName}</span>}
						{action.bidPackageName && <span>{action.bidPackageName}</span>}
						{action.decidedByName && <span>By {action.decidedByName}</span>}
						<time dateTime={action.decidedAt || action.updatedAt || action.createdAt}>
							{formatTime(action.decidedAt || action.updatedAt || action.createdAt)}
						</time>
					</div>
					<div className="autopilot-recent-action__status">
						<span className={accepted ? 'is-accepted' : 'is-declined'}>
							{accepted ? 'Accepted' : 'Declined'}
						</span>
						<span>{statusLabel(action.status)}</span>
					</div>
				</div>
				<span className="autopilot-recent-action__chevron" aria-hidden="true">
					{expanded ? <FaChevronUp /> : <FaChevronDown />}
				</span>
			</button>
			{expanded && (
				<div className="autopilot-recent-action__content" id={contentId}>
					{details.length ? (
						<div className="autopilot-recent-action__details">
							{details.map((detail) => (
								<div key={detail.label}>
									<strong>{detail.label}</strong>
									<p>{detail.value}</p>
								</div>
							))}
						</div>
					) : (
						<p>No description available.</p>
					)}
					{action.preview && (
						<Preview
							preview={action.preview}
							hideComparison={action.type === 'A1_START_INITIAL_AUTO_ANALYSIS'}
						/>
					)}
					<Citations citations={action.citations} />
				</div>
			)}
		</article>
	);
};

const RecentActionsModal: FC<{
	activityNotifications: ActivityNotification[];
	initialActions: AutopilotAction[];
	onCache: (actions: AutopilotAction[]) => void;
	onClose: () => void;
	organizationId: number | null;
	projectId: number | null;
	scope: 'PROJECT' | 'ORGANIZATION';
}> = ({
	activityNotifications,
	initialActions,
	onCache,
	onClose,
	organizationId,
	projectId,
	scope,
}) => {
	const dialogRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const [actions, setActions] = useState(initialActions);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState('');
	const [requestVersion, setRequestVersion] = useState(0);
	const [expandedActionIds, setExpandedActionIds] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState<'actions' | 'activity'>('actions');
	const userSelectedTabRef = useRef(false);
	const actionTabRef = useRef<HTMLButtonElement>(null);
	const activityTabRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		let mounted = true;
		const source = createAutopilotCancelSource();
		setLoading(true);
		setError('');
		getAutopilotRecentActions(null, 50, source.token, projectId, organizationId, scope)
			.then((page) => {
				if (!mounted) return;
				const normalizedActions = mergeAutopilotActionsByIdentity(page.actions);
				setActions(normalizedActions);
				setNextCursor(page.nextCursor || null);
				onCache(normalizedActions);
				if (
					!normalizedActions.length &&
					activityNotifications.length &&
					!userSelectedTabRef.current
				) {
					setActiveTab('activity');
				}
			})
			.catch((requestError: any) => {
				if (!mounted || requestError?.__CANCEL__) return;
				setError(
					requestError?.response?.data?.message ||
						requestError?.data?.message ||
						'Recent actions could not be loaded. Try again.',
				);
			})
			.finally(() => {
				if (mounted) setLoading(false);
			});
		return () => {
			mounted = false;
			source.cancel('Recent Actions closed');
		};
		// onCache is intentionally excluded so a parent render does not restart the request.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organizationId, projectId, requestVersion, scope]);

	const loadMore = async () => {
		if (!nextCursor || loadingMore) return;
		setLoadingMore(true);
		setError('');
		try {
			const page = await getAutopilotRecentActions(
				nextCursor,
				50,
				undefined,
				projectId,
				organizationId,
				scope,
			);
			setActions((current) => {
				const values = mergeAutopilotActionsByIdentity(current, page.actions);
				onCache(values);
				return values;
			});
			setNextCursor(page.nextCursor || null);
		} catch (requestError) {
			const value = requestError as any;
			setError(
				value?.response?.data?.message ||
					value?.data?.message ||
					'More recent actions could not be loaded. Try again.',
			);
		} finally {
			setLoadingMore(false);
		}
	};

	useEffect(() => {
		previousFocusRef.current = document.activeElement as HTMLElement;
		const previousBodyOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const restoreBackground = isolateModalBackground(dialogRef.current);
		const frame = window.requestAnimationFrame(() => {
			(dialogRef.current?.querySelector('button') as HTMLElement | null)?.focus();
		});
		return () => {
			window.cancelAnimationFrame(frame);
			restoreBackground();
			document.body.style.overflow = previousBodyOverflow;
			previousFocusRef.current?.focus();
		};
	}, []);

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			onClose();
			return;
		}
		if (event.key !== 'Tab') return;
		const focusable = Array.from(
			dialogRef.current?.querySelectorAll<HTMLElement>(PANEL_FOCUSABLE_SELECTOR) || [],
		).filter(
			(element) => !element.hasAttribute('disabled') && !element.closest<HTMLElement>('[hidden]'),
		);
		if (!focusable.length) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	const selectTab = (tab: 'actions' | 'activity', userInitiated = true) => {
		if (userInitiated) userSelectedTabRef.current = true;
		setActiveTab(tab);
	};

	const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		let target: 'actions' | 'activity' | null = null;
		if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
			target = activeTab === 'actions' ? 'activity' : 'actions';
		} else if (event.key === 'Home') {
			target = 'actions';
		} else if (event.key === 'End') {
			target = 'activity';
		}
		if (!target) return;
		event.preventDefault();
		selectTab(target);
		(target === 'actions' ? actionTabRef : activityTabRef).current?.focus();
	};

	const modal = (
		<div className="autopilot-recent-modal__backdrop">
			<div
				className="autopilot-recent-modal"
				aria-labelledby="autopilot-recent-actions-heading"
				aria-modal="true"
				onKeyDown={onKeyDown}
				ref={dialogRef}
				role="dialog"
			>
				<header>
					<div>
						<h2 id="autopilot-recent-actions-heading">Recent actions</h2>
						<p>Completed actions and activity updates in the selected project scope.</p>
					</div>
					<button type="button" onClick={onClose} aria-label="Close recent actions">
						<FaTimes />
					</button>
				</header>
				<div
					aria-label="Recent actions sections"
					className="autopilot-recent-modal__tabs"
					role="tablist"
				>
					<button
						aria-controls="autopilot-recent-actions-panel"
						aria-selected={activeTab === 'actions'}
						id="autopilot-recent-actions-tab"
						onClick={() => selectTab('actions')}
						onKeyDown={onTabKeyDown}
						ref={actionTabRef}
						role="tab"
						tabIndex={activeTab === 'actions' ? 0 : -1}
						type="button"
					>
						Action cards <span>{actions.length}</span>
					</button>
					<button
						aria-controls="autopilot-recent-activity-panel"
						aria-selected={activeTab === 'activity'}
						id="autopilot-recent-activity-tab"
						onClick={() => selectTab('activity')}
						onKeyDown={onTabKeyDown}
						ref={activityTabRef}
						role="tab"
						tabIndex={activeTab === 'activity' ? 0 : -1}
						type="button"
					>
						Activity updates <span>{activityNotifications.length}</span>
					</button>
				</div>
				<div className="autopilot-recent-modal__list">
					<section
						aria-labelledby="autopilot-recent-actions-tab"
						className="autopilot-recent-modal__panel"
						hidden={activeTab !== 'actions'}
						id="autopilot-recent-actions-panel"
						role="tabpanel"
					>
						{error && (
							<div className="autopilot-recent-modal__error" role="alert">
								<span>{error}</span>
								<button type="button" onClick={() => setRequestVersion((value) => value + 1)}>
									Retry
								</button>
							</div>
						)}
						{loading && !actions.length ? (
							<LoadingState />
						) : (
							<section
								aria-labelledby="autopilot-recent-action-cards-heading"
								className="autopilot-recent-deck-section"
							>
								<div className="autopilot-recent-deck-section__heading">
									<h3 id="autopilot-recent-action-cards-heading">Action cards</h3>
									<span>{actions.length}</span>
								</div>
								{actions.length ? (
									<div
										aria-label="Recent action card deck"
										className="autopilot-recent-action-deck"
										role="list"
									>
										{actions.map((action) => (
											<RecentActionItem
												action={action}
												expanded={expandedActionIds.includes(String(action.id))}
												key={String(action.id)}
												onToggle={() =>
													setExpandedActionIds((current) =>
														current.includes(String(action.id))
															? current.filter((id) => id !== String(action.id))
															: current.concat(String(action.id)),
													)
												}
											/>
										))}
									</div>
								) : (
									<p className="autopilot-recent-deck-section__empty">
										No completed action cards in this scope.
									</p>
								)}
								{nextCursor && (
									<button
										className="autopilot-recent-modal__more"
										type="button"
										onClick={loadMore}
										disabled={loadingMore}
									>
										{loadingMore ? 'Loading…' : 'Load more'}
									</button>
								)}
							</section>
						)}
					</section>
					<section
						aria-labelledby="autopilot-recent-activity-tab"
						className="autopilot-recent-modal__panel"
						hidden={activeTab !== 'activity'}
						id="autopilot-recent-activity-panel"
						role="tabpanel"
					>
						{activityNotifications.length ? (
							<RecentActivityUpdates notifications={activityNotifications} />
						) : (
							<p className="autopilot-recent-modal__empty">No activity updates in this scope.</p>
						)}
					</section>
				</div>
			</div>
		</div>
	);
	return typeof document === 'undefined' ? modal : createPortal(modal, document.body);
};

const ActivityDetails: FC<{ details: ActivityNotificationDetail[] }> = ({ details }) => (
	<div className="autopilot-activity__details" aria-label="Activity changes">
		{details.map((detail, index) => (
			<div
				className={`autopilot-activity__detail autopilot-activity__detail--${detail.kind.toLowerCase()}`}
				key={`${detail.kind}-${index}-${detail.text}`}
			>
				<strong>{detail.kind === 'ADDED' ? 'Added' : 'Deleted'}</strong>
				<span>{detail.text}</span>
			</div>
		))}
	</div>
);

const RecentActivityUpdates: FC<{ notifications: ActivityNotification[] }> = ({
	notifications,
}) => {
	const { viewActivityNotification } = useAutopilot();
	const [expandedIds, setExpandedIds] = useState<string[]>([]);
	if (!notifications.length) return null;
	return (
		<section
			className="autopilot-section autopilot-activity"
			aria-labelledby="autopilot-recent-activity-heading"
		>
			<div className="autopilot-section__heading autopilot-section__heading--activity">
				<div>
					<FaCheckCircle aria-hidden="true" />
					<h3 id="autopilot-recent-activity-heading">Activity updates</h3>
				</div>
				<span>{notifications.length}</span>
			</div>
			<div
				aria-label="Recent activity update deck"
				className="autopilot-activity__list autopilot-activity__list--deck"
				role="list"
			>
				{notifications.map((notification) => {
					const key = String(notification.recipientId);
					const expanded = expandedIds.includes(key);
					const contentId = `autopilot-recent-activity-${key}`;
					return (
						<article className="autopilot-activity__card" key={key} role="listitem">
							<button
								aria-controls={contentId}
								aria-expanded={expanded}
								className="autopilot-activity__disclosure"
								onClick={() =>
									setExpandedIds((current) =>
										expanded ? current.filter((item) => item !== key) : current.concat(key),
									)
								}
								type="button"
							>
								<div className="autopilot-activity__summary">
									<div className="autopilot-activity__topline">
										<div>
											<strong title={notification.projectName}>{notification.projectName}</strong>
											{notification.isRevision && <span>Revision</span>}
										</div>
										<time dateTime={notification.occurredAt}>
											{formatTime(notification.occurredAt)}
										</time>
									</div>
									<h3>{notification.title}</h3>
								</div>
								<span className="autopilot-activity__chevron" aria-hidden="true">
									{expanded ? <FaChevronUp /> : <FaChevronDown />}
								</span>
							</button>
							{expanded && (
								<div className="autopilot-activity__content" id={contentId}>
									<p>{notification.message}</p>
									{notification.details.length > 0 && (
										<ActivityDetails details={notification.details} />
									)}
									<div className="autopilot-activity__actions">
										<button type="button" onClick={() => viewActivityNotification(notification)}>
											View <FaArrowRight />
										</button>
									</div>
								</div>
							)}
						</article>
					);
				})}
			</div>
		</section>
	);
};

const EMPTY_AUTOPILOT_ARTIFACTS: AutopilotArtifact[] = [];

const ArtifactCards: FC<{
	artifacts?: AutopilotArtifact[];
	onPreview: (artifact: AutopilotArtifact, opener: HTMLElement) => void;
}> = ({ artifacts, onPreview }) => {
	const normalizedArtifacts = artifacts || EMPTY_AUTOPILOT_ARTIFACTS;
	const [rows, setRows] = useState(normalizedArtifacts);
	const [busyId, setBusyId] = useState<string>('');
	const [error, setError] = useState('');
	const artifactKey = normalizedArtifacts
		.map((artifact) => `${artifact.id}:${artifact.status}:${artifact.previewStatus}`)
		.join('|');

	useEffect(() => setRows(normalizedArtifacts), [artifactKey, normalizedArtifacts]);

	useEffect(() => {
		if (!rows.some((row) => ['PENDING', 'GENERATING'].includes(row.status))) return undefined;
		let active = true;
		const timer = window.setInterval(async () => {
			try {
				const refreshed = await Promise.all(
					rows.map((row) =>
						['PENDING', 'GENERATING'].includes(row.status)
							? getAutopilotArtifact(row.id)
							: Promise.resolve(row),
					),
				);
				if (active) setRows(refreshed);
			} catch (_error) {
				if (active) setError('Artifact status could not be refreshed.');
			}
		}, 2500);
		return () => {
			active = false;
			window.clearInterval(timer);
		};
	}, [rows]);

	if (!rows.length) return null;

	const download = async (artifact: AutopilotArtifact) => {
		setBusyId(String(artifact.id));
		setError('');
		try {
			const blob = await loadAutopilotArtifact(artifact.id, false);
			const url = window.URL.createObjectURL(blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = artifact.fileName;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
		} catch (_error) {
			setError('The file could not be downloaded.');
		} finally {
			setBusyId('');
		}
	};

	const retry = async (artifact: AutopilotArtifact) => {
		setBusyId(String(artifact.id));
		setError('');
		try {
			const refreshed = await retryAutopilotArtifact(artifact.id);
			setRows((current) =>
				current.map((row) => (String(row.id) === String(refreshed.id) ? refreshed : row)),
			);
		} catch (_error) {
			setError('The artifact could not be retried.');
		} finally {
			setBusyId('');
		}
	};

	return (
		<div className="autopilot-artifacts" aria-label="Generated files">
			{rows.map((artifact) => {
				const busy = busyId === String(artifact.id);
				return (
					<div className="autopilot-artifact" key={String(artifact.id)}>
						<FaFileAlt aria-hidden="true" />
						<div>
							<strong>{artifact.fileName}</strong>
							<small>
								{artifact.format} · Version {artifact.version} ·{' '}
								{artifact.status === 'READY'
									? 'Ready'
									: artifact.status === 'FAILED'
									? 'Generation failed'
									: 'Generating…'}
							</small>
						</div>
						<div className="autopilot-artifact__actions">
							{artifact.status === 'READY' && artifact.previewStatus === 'READY' && (
								<button
									type="button"
									onClick={(event: MouseEvent<HTMLButtonElement>) =>
										onPreview(artifact, event.currentTarget)
									}
								>
									Preview
								</button>
							)}
							{artifact.status === 'READY' && (
								<button type="button" disabled={busy} onClick={() => download(artifact)}>
									<FaDownload aria-hidden="true" /> Download
								</button>
							)}
							{artifact.status === 'FAILED' && (
								<button type="button" disabled={busy} onClick={() => retry(artifact)}>
									{busy ? 'Retrying…' : 'Retry'}
								</button>
							)}
						</div>
					</div>
				);
			})}
			{error && <span className="autopilot-artifacts__error">{error}</span>}
		</div>
	);
};

export const ArtifactPreview: FC<{
	artifact: AutopilotArtifact;
	onDismiss: () => void;
}> = ({ artifact, onDismiss }) => {
	const [url, setUrl] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(true);
	const [zoom, setZoom] = useState(100);
	const [pageCount, setPageCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageWidth, setPageWidth] = useState(720);
	const [closing, setClosing] = useState(false);
	const [pdfRenderer, setPdfRenderer] = useState<any>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const previewRef = useRef<HTMLElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const closeTimerRef = useRef<number | null>(null);
	const PdfDocument = pdfRenderer?.Document;
	const PdfPage = pdfRenderer?.Page;

	useEffect(() => {
		closeButtonRef.current?.focus();
	}, []);

	useEffect(() => {
		let active = true;
		import('react-pdf')
			.then((renderer) => {
				renderer.pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${renderer.pdfjs.version}/pdf.worker.js`;
				if (active) setPdfRenderer({ Document: renderer.Document, Page: renderer.Page });
			})
			.catch(() => active && setError('The read-only PDF viewer could not be loaded.'));
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		let active = true;
		let objectUrl = '';
		setLoading(true);
		setError('');
		loadAutopilotArtifact(artifact.id, true)
			.then((blob) => {
				if (!active) return;
				objectUrl = window.URL.createObjectURL(blob);
				setUrl(objectUrl);
			})
			.catch(() => active && setError('The PDF preview could not be loaded.'))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
			if (objectUrl) window.URL.revokeObjectURL(objectUrl);
		};
	}, [artifact.id]);

	useEffect(() => {
		const resize = () => {
			const available = bodyRef.current?.clientWidth || Math.min(window.innerWidth, 960);
			setPageWidth(Math.max(260, Math.min(900, available - (window.innerWidth < 768 ? 24 : 48))));
		};
		resize();
		window.addEventListener('resize', resize);
		return () => window.removeEventListener('resize', resize);
	}, [url]);

	useEffect(
		() => () => {
			if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
		},
		[],
	);

	const collapsePreview = () => {
		if (closing) return;
		setClosing(true);
		const reduceMotion =
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (reduceMotion) {
			onDismiss();
			return;
		}
		closeTimerRef.current = window.setTimeout(onDismiss, 180);
	};

	const onPreviewKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key !== 'Escape' || event.defaultPrevented) return;
		const nestedSurface = previewRef.current?.querySelector(
			'[role="menu"]:not([hidden]), [role="dialog"]:not(.autopilot-artifact-preview):not([hidden])',
		);
		if (nestedSurface) return;
		event.preventDefault();
		event.stopPropagation();
		collapsePreview();
	};

	const previousPage = () => setPageNumber((current) => Math.max(1, current - 1));
	const nextPage = () => setPageNumber((current) => Math.min(Math.max(1, pageCount), current + 1));

	return (
		<section
			className={`autopilot-artifact-preview${closing ? ' is-closing' : ''}`}
			aria-label={`${artifact.fileName} preview`}
			aria-live="polite"
			onKeyDown={onPreviewKeyDown}
			ref={previewRef}
			tabIndex={-1}
		>
			<header>
				<div className="autopilot-artifact-preview__identity">
					<span aria-hidden="true">
						<FaFileAlt />
					</span>
					<div>
						<strong>{artifact.fileName}</strong>
						<small>Read-only PDF preview</small>
					</div>
				</div>
				<button
					type="button"
					className="autopilot-artifact-preview__collapse"
					aria-label="Close preview"
					title="Close preview"
					onClick={collapsePreview}
					ref={closeButtonRef}
				>
					<FaTimes aria-hidden="true" />
				</button>
			</header>
			<div className="autopilot-artifact-preview__toolbar">
				<div className="autopilot-artifact-preview__pagination" role="group" aria-label="PDF pages">
					<button type="button" onClick={previousPage} disabled={pageNumber <= 1}>
						Previous
					</button>
					<output>
						Page {pageNumber} of {pageCount || '—'}
					</output>
					<button type="button" onClick={nextPage} disabled={!pageCount || pageNumber >= pageCount}>
						Next
					</button>
				</div>
				<div className="autopilot-artifact-preview__controls" role="group" aria-label="PDF zoom">
					<button
						type="button"
						aria-label="Zoom out"
						disabled={zoom <= 50}
						onClick={() => setZoom((value) => Math.max(50, value - 10))}
					>
						−
					</button>
					<output>{zoom}%</output>
					<button
						type="button"
						aria-label="Zoom in"
						disabled={zoom >= 200}
						onClick={() => setZoom((value) => Math.min(200, value + 10))}
					>
						+
					</button>
				</div>
			</div>
			<div className="autopilot-artifact-preview__body" ref={bodyRef}>
				{(loading || (!pdfRenderer && !error)) && (
					<div className="autopilot-artifact-preview__message" role="status">
						<FaSpinner className="autopilot-spin" aria-hidden="true" />
						<span>Preparing your preview…</span>
					</div>
				)}
				{error && (
					<div className="autopilot-artifact-preview__message is-error" role="alert">
						<FaExclamationTriangle aria-hidden="true" />
						<span>{error}</span>
					</div>
				)}
				{url && PdfDocument && PdfPage && (
					<PdfDocument
						file={url}
						onLoadSuccess={({ numPages }: any) => {
							setPageCount(numPages);
							setPageNumber((current) => Math.min(Math.max(1, current), numPages));
						}}
						onLoadError={() => setError('The PDF preview could not be rendered.')}
						loading={undefined}
						error={undefined}
					>
						<div className="autopilot-artifact-preview__page" aria-label={`Page ${pageNumber}`}>
							<PdfPage
								pageNumber={pageNumber}
								width={Math.round((pageWidth * zoom) / 100)}
								renderAnnotationLayer={false}
								renderTextLayer={false}
								loading={undefined}
							/>
						</div>
					</PdfDocument>
				)}
			</div>
		</section>
	);
};

const Message: FC<{
	message: AutopilotMessage;
	onPreview: (artifact: AutopilotArtifact, opener: HTMLElement) => void;
	onRetry: (content: string) => void;
}> = ({ message, onPreview, onRetry }) => (
	<article className={`autopilot-message autopilot-message--${message.role.toLowerCase()}`}>
		<div className="autopilot-message__avatar" aria-hidden="true">
			{message.role === 'ASSISTANT' ? <FaRobot /> : message.role === 'USER' ? 'You' : 'i'}
		</div>
		<div className="autopilot-message__body">
			<div className="autopilot-message__heading">
				<strong>
					{message.role === 'ASSISTANT' ? 'ScrubPlan' : message.role === 'USER' ? 'You' : 'System'}
				</strong>
				<time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
			</div>
			{message.status === 'STREAMING' && message.role === 'ASSISTANT' ? (
				<div
					className="autopilot-message__loader"
					aria-label="ScrubPlan is responding"
					role="status"
				>
					<span />
					<span />
					<span />
					<span className="sr-only">ScrubPlan is responding…</span>
				</div>
			) : !(
					message.status === 'FAILED' &&
					message.role === 'ASSISTANT' &&
					message.content.trim() === String(message.errorMessage || '').trim()
			  ) ? (
				<p>
					{message.role === 'ASSISTANT'
						? conversationalAssistantText(message.content)
						: message.content}
				</p>
			) : null}
			<Citations citations={message.citations} />
			<ArtifactCards artifacts={message.artifacts} onPreview={onPreview} />
			{message.status === 'SENDING' && <span className="autopilot-message__state">Sending…</span>}
			{message.status === 'FAILED' && (
				<div className="autopilot-message__failed" role="alert">
					<span>{message.errorMessage || 'Message failed to send.'}</span>
					<button type="button" onClick={() => onRetry(message.retryContent || message.content)}>
						Try again
					</button>
				</div>
			)}
		</div>
	</article>
);

const ModeControl: FC = () => {
	const {
		currentProjectId,
		currentProjectSettings,
		modeBusy,
		modeError,
		organizationSettings,
		requestModeChange,
		selfDrivingReleaseEnabled,
	} = useAutopilot();
	const modes: AutomationMode[] = selfDrivingReleaseEnabled
		? ['MANUAL', 'AUTOPILOT', 'SELF_DRIVING']
		: ['MANUAL', 'AUTOPILOT'];
	const selectedMode =
		!selfDrivingReleaseEnabled && currentProjectSettings?.mode === 'SELF_DRIVING'
			? 'AUTOPILOT'
			: currentProjectSettings?.mode;

	return (
		<div className="autopilot-mode-block">
			<div className="autopilot-mode-block__label">
				<span>Project mode</span>
			</div>
			<div
				className="autopilot-mode"
				data-mode-count={modes.length}
				role="group"
				aria-label="Project automation mode"
			>
				{modes.map((mode) => {
					const disabled =
						!currentProjectId ||
						!currentProjectSettings ||
						modeBusy ||
						(mode === 'SELF_DRIVING' && organizationSettings?.selfDrivingAllowed !== true);
					return (
						<button
							aria-pressed={selectedMode === mode}
							className={selectedMode === mode ? 'is-active' : ''}
							disabled={disabled}
							key={mode}
							onClick={() => requestModeChange(mode)}
							title={
								!currentProjectId
									? 'Open a project to change its mode.'
									: mode === 'SELF_DRIVING' && organizationSettings?.selfDrivingAllowed !== true
									? organizationSettings
										? 'An organization administrator disabled Self-driving.'
										: 'Checking organization Self-driving availability.'
									: undefined
							}
							type="button"
						>
							{modeLabel(mode)}
						</button>
					);
				})}
			</div>
			{!currentProjectId && <small>Open a project to select an automation mode.</small>}
			{modeError && (
				<div className="autopilot-mode-block__error" role="alert">
					{modeError}
				</div>
			)}
		</div>
	);
};

const SelfDrivingWarningDialog: FC = () => {
	const {
		cancelSelfDriving,
		confirmSelfDriving,
		modeBusy,
		modeError,
		selfDrivingDialogOpen,
		selfDrivingReleaseEnabled,
	} = useAutopilot();
	const [acknowledged, setAcknowledged] = useState(false);
	const dialogRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!selfDrivingDialogOpen || !selfDrivingReleaseEnabled) {
			setAcknowledged(false);
			return undefined;
		}
		previousFocusRef.current = document.activeElement as HTMLElement;
		const restoreBackground = isolateModalBackground(dialogRef.current);
		const frame = window.requestAnimationFrame(() => {
			(dialogRef.current?.querySelector('input') as HTMLElement | null)?.focus();
		});
		return () => {
			window.cancelAnimationFrame(frame);
			restoreBackground();
			previousFocusRef.current?.focus();
		};
	}, [selfDrivingDialogOpen, selfDrivingReleaseEnabled]);

	if (!selfDrivingDialogOpen || !selfDrivingReleaseEnabled) return null;

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Escape' && !modeBusy) {
			event.preventDefault();
			cancelSelfDriving();
			return;
		}
		if (event.key !== 'Tab') return;
		const focusable = Array.from(
			dialogRef.current?.querySelectorAll<HTMLElement>(
				'button:not([disabled]), input:not([disabled])',
			) || [],
		);
		if (!focusable.length) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	return (
		<div className="autopilot-dialog-backdrop">
			<div
				aria-describedby="self-driving-warning-copy"
				aria-labelledby="self-driving-warning-title"
				aria-modal="true"
				className="autopilot-dialog"
				onKeyDown={onKeyDown}
				ref={dialogRef}
				role="dialog"
			>
				<div className="autopilot-dialog__icon" aria-hidden="true">
					<FaShieldAlt />
				</div>
				<h2 id="self-driving-warning-title">Enable Self-driving?</h2>
				<div id="self-driving-warning-copy">
					<p>
						ScrubPlan will make project decisions and take actions without waiting for your
						approval, including sending bidder messages, publishing project information, and
						advancing awards.
					</p>
					<ul>
						<li>Automated judgment can be incomplete or wrong.</li>
						<li>External messages may create schedule, cost, or relationship risk.</li>
						<li>Every action remains visible and security rules cannot be bypassed.</li>
					</ul>
				</div>
				<label className="autopilot-dialog__acknowledgement">
					<input
						type="checkbox"
						checked={acknowledged}
						onChange={(event) => setAcknowledged(event.target.checked)}
					/>
					<span>I understand these risks and authorize Self-driving for this project.</span>
				</label>
				{modeError && (
					<div className="autopilot-dialog__error" role="alert">
						{modeError}
					</div>
				)}
				<div className="autopilot-dialog__actions">
					<button type="button" onClick={cancelSelfDriving} disabled={modeBusy}>
						Cancel
					</button>
					<button
						className="autopilot-dialog__enable"
						type="button"
						disabled={!acknowledged || modeBusy}
						onClick={confirmSelfDriving}
					>
						{modeBusy ? 'Enabling…' : 'Enable Self-driving'}
					</button>
				</div>
			</div>
		</div>
	);
};

const LoadingState = () => (
	<div className="autopilot-loading" aria-label="Loading Autopilot Chat" role="status">
		<div />
		<div />
		<div />
		<span className="sr-only">Loading Autopilot Chat…</span>
	</div>
);

interface AutopilotPanelProps {
	modal: boolean;
	onClose: () => void;
}

const AutopilotPanel: FC<AutopilotPanelProps> = ({ modal, onClose }) => {
	const {
		actionableCount,
		actions,
		activityError,
		activityNotifications,
		currentProjectId,
		draft,
		error,
		focusRequest,
		hasUnsavedCardDrafts,
		lastUpdatedAt,
		loading,
		messages,
		offline,
		pendingCount,
		questionBusy,
		questionScope,
		refresh,
		refreshing,
		sendQuestion,
		stopQuestion,
		setDraft,
		setQuestionScope,
		selectedOrganizationId,
	} = useAutopilot();
	const composerRef = useRef<HTMLTextAreaElement>(null);
	const conversationBottomRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLElement>(null);
	const timelineRef = useRef<HTMLDivElement>(null);
	const [escapeArmed, setEscapeArmed] = useState(false);
	const [recentActionsOpen, setRecentActionsOpen] = useState(false);
	const [recentActionsCache, setRecentActionsCache] = useState<AutopilotAction[]>([]);
	const [chatFontSize, setChatFontSize] = useState(readAutopilotChatFontSize);
	const [previewArtifact, setPreviewArtifact] = useState<AutopilotArtifact | null>(null);
	const previewReturnFocusRef = useRef<HTMLElement | null>(null);
	const scopedActions = actions.filter((action) => {
		if (!action.projectId) return false;
		if (action.status === 'STALE' && action.acknowledgementAvailable !== true) return false;
		return questionScope === 'ORGANIZATION' || action.projectId === currentProjectId;
	});
	const pending = scopedActions.filter((action) => action.status === 'PENDING_APPROVAL');
	const attention = scopedActions.filter(
		(action) => action.status !== 'PENDING_APPROVAL' && requiresAutopilotAttention(action),
	);
	const inProgress = scopedActions.filter((action) =>
		['APPROVED', 'ANALYZING', 'EXECUTING'].includes(action.status),
	);
	const completedActions = scopedActions.filter(
		(action) =>
			!requiresAutopilotAttention(action) &&
			!['APPROVED', 'ANALYZING', 'EXECUTING'].includes(action.status),
	);
	const scopedActivityNotifications = activityNotifications.filter(
		(notification) =>
			questionScope === 'ORGANIZATION' || notification.projectId === currentProjectId,
	);
	const recentScopeKey = `${questionScope}:${currentProjectId || ''}:${
		selectedOrganizationId || ''
	}`;
	const recentSeed = mergeAutopilotActionsByIdentity(
		recentActionsCache,
		completedActions.filter((action) => action.decision),
	);
	const visibleMessages = messages.filter(
		(message) =>
			questionScope === 'ORGANIZATION' ||
			!currentProjectId ||
			!message.projectId ||
			message.projectId === currentProjectId,
	);
	const latestVisibleMessage = visibleMessages[visibleMessages.length - 1];
	const organizationSelectionRequired = questionScope === 'ORGANIZATION' && !selectedOrganizationId;
	const connectionError = error || activityError;
	const connectionState = offline
		? 'offline'
		: !lastUpdatedAt && loading
		? 'connecting'
		: connectionError && refreshing
		? 'reconnecting'
		: connectionError
		? 'attention'
		: 'connected';
	const connectionLabel =
		connectionState === 'offline'
			? 'Offline'
			: connectionState === 'connecting'
			? 'Connecting'
			: connectionState === 'reconnecting'
			? 'Reconnecting'
			: connectionState === 'attention'
			? 'Needs attention'
			: 'Connected';
	const visibleMessageCount = visibleMessages.length;
	const latestVisibleMessageId = latestVisibleMessage ? String(latestVisibleMessage.id) : '';
	const latestVisibleMessageStatus = latestVisibleMessage?.status;
	const latestVisibleMessageContent = latestVisibleMessage?.content;
	const latestVisibleMessageError = latestVisibleMessage?.errorMessage;

	useEffect(() => {
		setRecentActionsCache([]);
	}, [recentScopeKey]);

	useEffect(() => {
		if (focusRequest > 0) composerRef.current?.focus();
	}, [focusRequest]);

	const openArtifactPreview = (artifact: AutopilotArtifact, opener?: HTMLElement) => {
		previewReturnFocusRef.current = opener || composerRef.current;
		setPreviewArtifact(artifact);
	};

	const dismissArtifactPreview = () => {
		const returnFocus = previewReturnFocusRef.current || composerRef.current;
		setPreviewArtifact(null);
		previewReturnFocusRef.current = null;
		window.requestAnimationFrame(() => returnFocus?.focus());
	};

	useEffect(() => {
		if (!visibleMessageCount) return;
		if (
			questionBusy ||
			latestVisibleMessageId.startsWith('local-') ||
			latestVisibleMessageStatus === 'STREAMING' ||
			latestVisibleMessageStatus === 'FAILED'
		) {
			window.requestAnimationFrame(() => {
				const bottomAnchor = conversationBottomRef.current;
				if (bottomAnchor && typeof bottomAnchor.scrollIntoView === 'function') {
					bottomAnchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
				}
			});
		}
	}, [
		latestVisibleMessageContent,
		latestVisibleMessageError,
		latestVisibleMessageId,
		latestVisibleMessageStatus,
		questionBusy,
		visibleMessageCount,
	]);

	useEffect(() => {
		if (!escapeArmed) return undefined;
		const timeout = window.setTimeout(() => setEscapeArmed(false), 2500);
		return () => window.clearTimeout(timeout);
	}, [escapeArmed]);

	const onPanelKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Tab' && modal) {
			const focusable = Array.from(
				panelRef.current?.querySelectorAll<HTMLElement>(PANEL_FOCUSABLE_SELECTOR) || [],
			).filter(
				(element) =>
					!element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true',
			);
			if (focusable.length) {
				const first = focusable[0];
				const last = focusable[focusable.length - 1];
				if (event.shiftKey && document.activeElement === first) {
					event.preventDefault();
					last.focus();
				} else if (!event.shiftKey && document.activeElement === last) {
					event.preventDefault();
					first.focus();
				}
			}
			return;
		}
		if (event.key !== 'Escape') return;
		if (previewArtifact) {
			event.preventDefault();
			dismissArtifactPreview();
			return;
		}
		if ((draft.trim() || hasUnsavedCardDrafts) && !escapeArmed) {
			event.preventDefault();
			setEscapeArmed(true);
			return;
		}
		onClose();
	};

	const submit = (event: FormEvent) => {
		event.preventDefault();
		sendQuestion();
	};

	const adjustChatFontSize = (change: number) => {
		setChatFontSize((current) => {
			const next = Math.min(
				MAX_AUTOPILOT_CHAT_FONT_SIZE,
				Math.max(MIN_AUTOPILOT_CHAT_FONT_SIZE, current + change),
			);
			try {
				window.localStorage.setItem(AUTOPILOT_CHAT_FONT_SIZE_STORAGE_KEY, String(next));
			} catch (_error) {
				// The preference is still applied for this session when browser storage is unavailable.
			}
			return next;
		});
	};

	const panelStyle = {
		'--autopilot-chat-text-size': `${chatFontSize}px`,
	} as CSSProperties;

	return (
		<aside
			className="autopilot-panel"
			aria-label="ScrubPlan Autopilot Chat"
			aria-modal={modal || undefined}
			onKeyDown={onPanelKeyDown}
			ref={panelRef}
			role={modal ? 'dialog' : undefined}
			style={panelStyle}
		>
			<header className="autopilot-panel__header">
				<div className="autopilot-panel__brand">
					<span className="autopilot-panel__logo" aria-hidden="true">
						<FaRobot />
					</span>
					<div>
						<strong>ScrubPlan</strong>
						<small
							className={`autopilot-panel__status autopilot-panel__status--${connectionState}`}
							role="status"
							aria-live="polite"
							aria-atomic="true"
						>
							<span aria-hidden="true" />
							Autopilot Chat · {connectionLabel}
						</small>
					</div>
				</div>
				<div className="autopilot-panel__header-actions">
					{actionableCount > 0 && (
						<span
							className="autopilot-panel__pending-count"
							aria-label={`${actionableCount} ${
								actionableCount === 1 ? 'item needs' : 'items need'
							} attention`}
						>
							<strong>{actionableCount}</strong>
							<span>{actionableCount === pendingCount ? ' pending' : ' attention'}</span>
						</span>
					)}
					<button type="button" onClick={onClose} aria-label="Collapse Autopilot Chat">
						<FaTimes />
					</button>
				</div>
			</header>

			<ModeControl />

			{(offline || connectionError) && (
				<div
					className={`autopilot-connection${offline ? ' autopilot-connection--offline' : ''}`}
					role={connectionError ? 'alert' : 'status'}
				>
					<div>
						<strong>{offline ? 'You are offline' : 'Unable to refresh'}</strong>
						<span>
							{offline
								? 'Showing the last saved update. Actions will revalidate before running.'
								: connectionError}
						</span>
					</div>
					<button type="button" onClick={() => refresh()} disabled={refreshing || offline}>
						{refreshing ? 'Retrying…' : 'Retry'}
					</button>
				</div>
			)}

			<div className="autopilot-panel__timeline" ref={timelineRef}>
				{loading &&
				!visibleMessages.length &&
				!scopedActions.length &&
				!scopedActivityNotifications.length ? (
					<LoadingState />
				) : (
					<>
						<section className="autopilot-section" aria-labelledby="autopilot-conversation-heading">
							<div className="autopilot-section__heading autopilot-section__heading--plain">
								<h2 id="autopilot-conversation-heading">Conversation</h2>
							</div>
							{visibleMessages.length ? (
								visibleMessages.map((message) => (
									<Message
										key={String(message.id)}
										message={message}
										onPreview={openArtifactPreview}
										onRetry={(content) => sendQuestion(content)}
									/>
								))
							) : (
								<div className="autopilot-welcome">
									<span aria-hidden="true">
										<FaRobot />
									</span>
									<h3>What can I help you move forward?</h3>
									<p>
										Ask about plans, specifications, supplemental documents, scope, bids, or next
										steps.
									</p>
									<div>
										<button
											type="button"
											onClick={() => sendQuestion('What needs my attention on this project?')}
											disabled={!currentProjectId}
										>
											What needs attention?
										</button>
										<button
											type="button"
											onClick={() =>
												sendQuestion(
													'Summarize the latest project changes and cite the source documents.',
												)
											}
											disabled={!currentProjectId}
										>
											Summarize changes
										</button>
									</div>
								</div>
							)}
							{questionBusy && (
								<span className="sr-only" role="status" aria-live="polite">
									Reviewing authorized sources…
								</span>
							)}
							<div aria-hidden="true" ref={conversationBottomRef} />
						</section>

						{pending.length > 0 && (
							<section className="autopilot-section" aria-labelledby="autopilot-approvals-heading">
								<div className="autopilot-section__heading">
									<div>
										<span className="autopilot-section__pulse" />
										<h2 id="autopilot-approvals-heading">Needs your approval</h2>
									</div>
									<span>{pending.length}</span>
								</div>
								<ActionCardStack actions={pending} directTextEditing={!modal} label="Approval" />
							</section>
						)}

						{inProgress.length > 0 && (
							<section className="autopilot-section" aria-labelledby="autopilot-progress-heading">
								<div className="autopilot-section__heading autopilot-section__heading--progress">
									<div>
										<FaSpinner className="autopilot-spin" aria-hidden="true" />
										<h2 id="autopilot-progress-heading">In progress</h2>
									</div>
									<span>{inProgress.length}</span>
								</div>
								<ActionCardStack
									actions={inProgress}
									directTextEditing={!modal}
									label="In progress"
								/>
							</section>
						)}

						{attention.length > 0 && (
							<section className="autopilot-section" aria-labelledby="autopilot-attention-heading">
								<div className="autopilot-section__heading autopilot-section__heading--attention">
									<div>
										<FaExclamationTriangle aria-hidden="true" />
										<h2 id="autopilot-attention-heading">Needs attention</h2>
									</div>
									<span>{attention.length}</span>
								</div>
								<ActionCardStack actions={attention} directTextEditing={!modal} label="Attention" />
							</section>
						)}
					</>
				)}
			</div>

			{escapeArmed && (
				<div className="autopilot-draft-guard" role="status">
					{hasUnsavedCardDrafts
						? 'Card edits remain unsent. Press Escape again to close.'
						: 'Your draft is saved. Press Escape again to close.'}
				</div>
			)}
			<form className="autopilot-composer" onSubmit={submit}>
				<fieldset className="autopilot-composer__scope" aria-label="Search">
					<legend>Search</legend>
					<div className="autopilot-composer__scope-options">
						<button
							type="button"
							aria-pressed={questionScope === 'PROJECT'}
							className={questionScope === 'PROJECT' ? 'is-selected' : ''}
							disabled={!currentProjectId}
							onClick={() => setQuestionScope('PROJECT')}
						>
							Current project
						</button>
						<button
							type="button"
							aria-pressed={questionScope === 'ORGANIZATION'}
							className={questionScope === 'ORGANIZATION' ? 'is-selected' : ''}
							onClick={() => setQuestionScope('ORGANIZATION')}
						>
							All my projects
						</button>
					</div>
				</fieldset>
				<div className="autopilot-composer__input">
					<textarea
						aria-label="Ask ScrubPlan"
						placeholder="Ask about this project or tell ScrubPlan what to do…"
						ref={composerRef}
						rows={1}
						value={draft}
						disabled={organizationSelectionRequired}
						onChange={(event) => setDraft(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && !event.shiftKey) {
								event.preventDefault();
								sendQuestion();
							}
						}}
					/>
					{questionBusy ? (
						<button type="button" onClick={stopQuestion} aria-label="Stop response">
							<FaStop />
						</button>
					) : (
						<button
							type="submit"
							disabled={!draft.trim() || organizationSelectionRequired}
							aria-label="Send message"
						>
							<FaPaperPlane />
						</button>
					)}
				</div>
				<div className="autopilot-composer__footer">
					<button
						className="autopilot-composer__recent-actions"
						type="button"
						onClick={() => setRecentActionsOpen(true)}
					>
						<FaHistory aria-hidden="true" />
						Recent actions
						<span>{recentSeed.length + scopedActivityNotifications.length}</span>
					</button>
					<div className="autopilot-composer__text-size" role="group" aria-label="Chat font size">
						<span>Text size</span>
						<button
							aria-label="Decrease chat font size"
							disabled={chatFontSize <= MIN_AUTOPILOT_CHAT_FONT_SIZE}
							onClick={() => adjustChatFontSize(-1)}
							type="button"
						>
							−
						</button>
						<output aria-live="polite" aria-label={`Chat font size ${chatFontSize} pixels`}>
							{chatFontSize}px
						</output>
						<button
							aria-label="Increase chat font size"
							disabled={chatFontSize >= MAX_AUTOPILOT_CHAT_FONT_SIZE}
							onClick={() => adjustChatFontSize(1)}
							type="button"
						>
							+
						</button>
					</div>
					<small>
						ScrubPlan can make mistakes. Review cited source documents for important decisions.
					</small>
				</div>
			</form>
			{recentActionsOpen && (
				<RecentActionsModal
					activityNotifications={scopedActivityNotifications}
					initialActions={recentSeed}
					onCache={setRecentActionsCache}
					onClose={() => setRecentActionsOpen(false)}
					organizationId={selectedOrganizationId}
					projectId={currentProjectId}
					scope={questionScope}
				/>
			)}
			{previewArtifact && (
				<ArtifactPreview artifact={previewArtifact} onDismiss={dismissArtifactPreview} />
			)}
		</aside>
	);
};

interface Props {
	children: ReactNode;
}

const AutopilotShellLayout: FC<Props> = ({ children }) => {
	const {
		actionableCount,
		authenticated,
		closeRail,
		isOpen,
		liveAnnouncement,
		openRail,
		railWidth,
		resetRailWidth,
		selfDrivingDialogOpen,
		setRailWidth,
	} = useAutopilot();
	const { isOpen: multiViewOpen } = useMultiView();
	const shellRef = useRef<HTMLDivElement>(null);
	const workspaceRef = useRef<HTMLDivElement>(null);
	const launcherRef = useRef<HTMLButtonElement>(null);
	const reviewSurfaces = useAutopilotReviewSurfaces();
	const draggingRef = useRef(false);
	const [viewportWidth, setViewportWidth] = useState(() =>
		typeof window === 'undefined' ? 1440 : window.innerWidth,
	);
	const mobile = viewportWidth <= MOBILE_AUTOPILOT_BREAKPOINT;
	const maximumRailWidth =
		multiViewOpen && viewportWidth >= DOCKED_AUTOPILOT_BREAKPOINT
			? Math.max(
					MIN_AUTOPILOT_RAIL_WIDTH,
					Math.min(
						MAX_AUTOPILOT_RAIL_WIDTH,
						viewportWidth - AUTOPILOT_SEPARATOR_WIDTH - MULTI_VIEW_MIN_WORKSPACE_WIDTH,
					),
			  )
			: MAX_AUTOPILOT_RAIL_WIDTH;
	const displayedRailWidth = Math.min(railWidth, maximumRailWidth);
	const interactiveReviewerMounted =
		reviewSurfaces.some((surface) => surface.kind === 'REVIEWER') ||
		(typeof window !== 'undefined' && isInteractiveReviewerPath(window.location.pathname));
	const [launcherStyle, setLauncherStyle] = useState<CSSProperties>(() => ({
		right: mobile ? 12 : 0,
		top: interactiveReviewerMounted ? (mobile ? 144 : 164) : mobile ? 78 : 88,
	}));

	useEffect(() => {
		const updateViewportWidth = () => setViewportWidth(window.innerWidth);
		window.addEventListener('resize', updateViewportWidth);
		return () => window.removeEventListener('resize', updateViewportWidth);
	}, []);

	useEffect(() => {
		if (isOpen || typeof window === 'undefined') return undefined;
		let frame: number | null = null;
		const updatePlacement = () => {
			frame = null;
			const launcher = launcherRef.current;
			const launcherRect = launcher?.getBoundingClientRect();
			const visualViewport = (window as any).visualViewport as
				| {
						height?: number;
						width?: number;
				  }
				| undefined;
			const viewportHeight = visualViewport?.height || window.innerHeight;
			const viewportWidth = visualViewport?.width || window.innerWidth;
			const placement = getAutopilotLauncherPlacement({
				launcherHeight: launcherRect?.height || (mobile ? 48 : 46),
				launcherWidth: launcherRect?.width || (mobile ? 48 : 168),
				mobile,
				obstructions: reviewSurfaces
					.filter((surface) => surface.kind === 'OBSTRUCTION' && surface.element.isConnected)
					.map((surface) => surface.element.getBoundingClientRect()),
				reviewerMounted: interactiveReviewerMounted,
				viewportHeight,
				viewportWidth,
			});
			const launcherHeight = launcherRect?.height || (mobile ? 48 : 46);
			const nextStyle: CSSProperties = mobile
				? {
						right: `max(${placement.right}px, env(safe-area-inset-right))`,
						top: `clamp(calc(12px + env(safe-area-inset-top)), ${placement.top}px, calc(100dvh - ${
							launcherHeight + LAUNCHER_EDGE_GAP
						}px - env(safe-area-inset-bottom)))`,
				  }
				: placement;
			setLauncherStyle((current) =>
				current.top === nextStyle.top && current.right === nextStyle.right ? current : nextStyle,
			);
		};
		const schedulePlacement = () => {
			if (frame !== null) return;
			frame = window.requestAnimationFrame(updatePlacement);
		};
		const ResizeObserverCtor = (window as any).ResizeObserver;
		const observer = ResizeObserverCtor ? new ResizeObserverCtor(schedulePlacement) : null;
		reviewSurfaces.forEach((surface) => observer?.observe(surface.element));
		if (launcherRef.current) observer?.observe(launcherRef.current);
		window.addEventListener('resize', schedulePlacement);
		window.addEventListener('orientationchange', schedulePlacement);
		window.addEventListener('scroll', schedulePlacement, true);
		window.addEventListener('pointermove', schedulePlacement, true);
		window.addEventListener('mousemove', schedulePlacement, true);
		window.addEventListener('touchmove', schedulePlacement, true);
		const visualViewport = (window as any).visualViewport as
			| {
					addEventListener?: (type: string, listener: () => void) => void;
					removeEventListener?: (type: string, listener: () => void) => void;
			  }
			| undefined;
		visualViewport?.addEventListener?.('resize', schedulePlacement);
		visualViewport?.addEventListener?.('scroll', schedulePlacement);
		schedulePlacement();
		return () => {
			if (frame !== null) window.cancelAnimationFrame(frame);
			observer?.disconnect();
			window.removeEventListener('resize', schedulePlacement);
			window.removeEventListener('orientationchange', schedulePlacement);
			window.removeEventListener('scroll', schedulePlacement, true);
			window.removeEventListener('pointermove', schedulePlacement, true);
			window.removeEventListener('mousemove', schedulePlacement, true);
			window.removeEventListener('touchmove', schedulePlacement, true);
			visualViewport?.removeEventListener?.('resize', schedulePlacement);
			visualViewport?.removeEventListener?.('scroll', schedulePlacement);
		};
	}, [interactiveReviewerMounted, isOpen, mobile, reviewSurfaces]);

	useEffect(() => {
		const workspace = workspaceRef.current;
		if (!workspace) return undefined;
		if (!authenticated || !isOpen || (!mobile && !selfDrivingDialogOpen)) {
			workspace.removeAttribute('aria-hidden');
			workspace.removeAttribute('inert');
			return undefined;
		}
		workspace.setAttribute('aria-hidden', 'true');
		workspace.setAttribute('inert', '');
		return () => {
			workspace.removeAttribute('aria-hidden');
			workspace.removeAttribute('inert');
		};
	}, [authenticated, isOpen, mobile, selfDrivingDialogOpen]);

	const closeAndRestoreFocus = () => {
		closeRail();
		window.requestAnimationFrame(() => launcherRef.current?.focus());
	};

	useEffect(() => {
		const move = (event: PointerEvent) => {
			if (!draggingRef.current) return;
			const rect = shellRef.current?.getBoundingClientRect();
			if (!rect) return;
			setRailWidth(Math.min(maximumRailWidth, rect.right - event.clientX));
		};
		const end = () => {
			draggingRef.current = false;
			document.body.classList.remove('autopilot-is-resizing');
		};
		window.addEventListener('pointermove', move);
		window.addEventListener('pointerup', end);
		window.addEventListener('pointercancel', end);
		return () => {
			window.removeEventListener('pointermove', move);
			window.removeEventListener('pointerup', end);
			window.removeEventListener('pointercancel', end);
			document.body.classList.remove('autopilot-is-resizing');
		};
	}, [maximumRailWidth, setRailWidth]);

	if (!authenticated) return <>{children}</>;

	return (
		<div
			className="autopilot-shell"
			data-autopilot-open={isOpen ? 'true' : 'false'}
			ref={shellRef}
			style={{ '--autopilot-rail-width': `${displayedRailWidth}px` } as React.CSSProperties}
		>
			<div className="autopilot-shell__workspace" ref={workspaceRef}>
				{children}
			</div>
			<div className="autopilot-shell__backdrop" aria-hidden="true" />
			<div
				aria-label="Resize Autopilot Chat"
				aria-orientation="vertical"
				aria-valuemax={maximumRailWidth}
				aria-valuemin={MIN_AUTOPILOT_RAIL_WIDTH}
				aria-valuenow={displayedRailWidth}
				className="autopilot-shell__separator"
				onDoubleClick={resetRailWidth}
				onKeyDown={(event) => {
					const step = event.shiftKey ? 64 : 16;
					if (event.key === 'ArrowLeft') {
						event.preventDefault();
						setRailWidth(Math.min(maximumRailWidth, displayedRailWidth + step));
					} else if (event.key === 'ArrowRight') {
						event.preventDefault();
						setRailWidth(displayedRailWidth - step);
					} else if (event.key === 'Home') {
						event.preventDefault();
						setRailWidth(MIN_AUTOPILOT_RAIL_WIDTH);
					} else if (event.key === 'End') {
						event.preventDefault();
						setRailWidth(maximumRailWidth);
					}
				}}
				onPointerDown={(event) => {
					event.preventDefault();
					draggingRef.current = true;
					document.body.classList.add('autopilot-is-resizing');
				}}
				role="separator"
				tabIndex={isOpen ? 0 : -1}
			>
				<span aria-hidden="true" />
			</div>
			<AutopilotPanel modal={mobile && isOpen} onClose={closeAndRestoreFocus} />
			{!isOpen && (
				<button
					className={`autopilot-launcher${
						interactiveReviewerMounted ? ' autopilot-launcher--reviewer' : ''
					}`}
					ref={launcherRef}
					style={launcherStyle}
					type="button"
					onClick={() => openRail(true)}
					aria-label={`Open Autopilot Chat${
						actionableCount
							? `, ${actionableCount} ${
									actionableCount === 1 ? 'item needs' : 'items need'
							  } attention`
							: ''
					}`}
				>
					<FaRobot aria-hidden="true" />
					<span>Autopilot Chat</span>
					{actionableCount > 0 && <strong>{actionableCount > 99 ? '99+' : actionableCount}</strong>}
				</button>
			)}
			<div className="sr-only" aria-live="polite" aria-atomic="true">
				{liveAnnouncement}
			</div>
			<AutopilotTransientPreview />
			<SelfDrivingWarningDialog />
		</div>
	);
};

const AutopilotShell: FC<Props> = ({ children }) => (
	<AutopilotReviewSurfaceProvider>
		<AutopilotShellLayout>{children}</AutopilotShellLayout>
	</AutopilotReviewSurfaceProvider>
);

export default AutopilotShell;
